import { useState, useMemo, useEffect, useRef } from "react";
import {
  MagnifyingGlass,
  Trash,
  FloppyDisk,
  ArrowCounterClockwise,
  Stack,
  DownloadSimple,
  Info,
} from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { normalizeAttendancePattern } from "@/lib/studentProfileTaxonomy";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useFidelityPref } from "@/hooks/useFidelityPref";
import { SERVICE_BLOCK_MINUTE_OPTIONS } from "@/lib/serviceDurations";
import { TimeHeader, BuilderBlock, GradeLegend } from "./SharedComponents";
import BuilderValidationDrawer from "./BuilderValidationDrawer";
import GradeConstraintOverlay, {
  resolveGradeConstraintsForOverlay,
} from "./GradeConstraintOverlay";
import StudentConstraintOverlay, {
  resolveStudentConstraintsForOverlay,
} from "./StudentConstraintOverlay";
import {
  DAYS,
  HOURS,
  GRADE_COLORS,
  gradeColor,
  TIME_START,
  TIME_END,
  TOTAL_MIN,
  SNAP,
  toMin,
  toTime,
  pct,
  assignLanes,
  fmtTime,
  studentMonthlyMinutes,
  formatMonthlyMinutes,
} from "./utils";

const ROW_MIN_HEIGHT = 120; // px — fixed minimum row height for canvas feel
const LANE_PX = 34; // px per lane when overlaps push beyond min

export default function ScheduleBuilder({
  provider,
  allStudents,
  sessions,
  conflictSet,
  sharedConflictSet,
  sharedSchoolSessions = [],
  weekDates,
  activePeriod,
  onSaved,
  resetSignal,
  wizardDraft,
  onWizardDraftConsumed,
  highlightBlockId,
  focusStudentId,
  // FP3-106C — Provider-owned Clear All action (mounted by parent
  // page so it can also reset the Wizard draft state on success).
  onOpenClearAll,
}) {
  const [pendingChanges, setPendingChanges] = useState({});
  const [newSessions, setNewSessions] = useState([]);
  // FP3-109 — Per-block conflict details returned by the atomic Save
  // preflight. Rendered as an inline banner ABOVE the canvas so the
  // provider sees exactly which student / weekday / rule blocked the
  // Save (never a bare "409" toast).
  const [saveConflicts, setSaveConflicts] = useState([]);
  const [unplacedBlocks, setUnplacedBlocks] = useState([]);
  const [saving, setSaving] = useState(false);
  const [builderSearch, setBuilderSearch] = useState("");
  const [selectedStudent, setSelectedStudent] = useState(null);
  // FP3-075B — Simple Mode hides the global "Schedule is clean / N
  // validation issues" pill (and its drawer trigger). Manual scheduling
  // is the first-class workflow in Simple Mode, so global health-style
  // banners no longer dominate the canvas. Action-level safety checks
  // (save errors, end-before-start, etc.) are untouched.
  const { preferences } = useAuth();
  const isSimpleMode = preferences?.ui_mode === "simple";

  // Iter 33d Follow-up 6 — Manual-mode validation: after Save we run the
  // full health-check engine and surface violations inline. Local state
  // means the panel persists across the silent refresh and the user
  // never gets bounced out of Builder.
  const [validationViolations, setValidationViolations] = useState([]);
  const [validationLoading, setValidationLoading] = useState(false);
  const [validationLastRunAt, setValidationLastRunAt] = useState(null);
  // Iter 33d Follow-up 9 — drawer open state. Auto-opens after a save
  // that finds violations; user can also toggle it via the summary pill.
  const [validationDrawerOpen, setValidationDrawerOpen] = useState(false);

  // Iter 33d Follow-up 9 — Left-panel creator accordion. Replaces the
  // two always-visible Service/Personal cards. Auto-switches to the
  // service tab when a student is selected, otherwise defaults to
  // personal so the panel stays compact.
  const [creatorTab, setCreatorTab] = useState("personal");
  useEffect(() => {
    if (selectedStudent) setCreatorTab("service");
  }, [selectedStudent]);

  // FP3-065 follow-up #7 audit — Pre-warning for student-constraint
  // conflicts during manual drag/drop. Load every visible student's
  // constraint windows once on mount; the drag/drop handlers below
  // check the dragged student's constraint set against the candidate
  // slot and BLOCK the drop with a toast carrying the constraint
  // label. The backend validator still catches anything that slips
  // through (e.g. constraints created after this load).
  const [studentConstraints, setStudentConstraints] = useState([]);
  // FP3-065 follow-up #7 audit — Same pattern for grade-level
  // school rules. `grade_schedules.patterns.<pattern>.constraints[]`
  // ships every per-(school, grade, attendance_pattern) window the
  // wizard already honours; we mirror that check on the client.
  const [gradeSchedules, setGradeSchedules] = useState([]);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [scRes, gsRes] = await Promise.all([
          api.listStudentConstraints(),
          api.listGradeSchedules(),
        ]);
        if (!cancelled) {
          setStudentConstraints(scRes.data || []);
          setGradeSchedules(gsRes.data || []);
        }
      } catch {
        if (!cancelled) {
          setStudentConstraints([]);
          setGradeSchedules([]);
        }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Returns the FIRST overlapping constraint for (studentId, day,
  // startMin, endMin) or null. Used to short-circuit drop AND show
  // the hover warning chip.
  //
  // FP3-107A hotfix — Multi-day constraints. Canonical shape is
  // `days: [int]`; legacy shape is a single `day_of_week: int`. A
  // Mon/Wed/Fri rule was previously being silently ignored on Wed
  // and Fri (only the first day matched `sc.day_of_week`). We now
  // consume the canonical array with a legacy fallback so every
  // selected weekday blocks the drop.
  const findStudentConstraintConflict = (studentId, day, sm, em) => {
    if (!studentId || day == null) return null;
    for (const sc of studentConstraints) {
      if (sc.student_id !== studentId) continue;
      const rawDays = Array.isArray(sc.days) && sc.days.length
        ? sc.days
        : (typeof sc.day_of_week === "number" ? [sc.day_of_week] : []);
      const scDays = new Set(rawDays.filter((d) => d >= 0 && d <= 4));
      if (!scDays.has(day)) continue;
      const cs = toMin(sc.start_time);
      const ce = toMin(sc.end_time);
      if (sm < ce && cs < em) return sc;
    }
    return null;
  };

  // FP3-065 follow-up #7 audit — Pre-warning for provider personal
  // rules during manual drag/drop. Reuses the existing `sessions`
  // prop (already projected per-week with weeks_of_month honoured
  // by the backend) so no new fetch is required. Returns the FIRST
  // personal block that overlaps (dayIdx, sm, em) for the dragged
  // provider.
  const findProviderRuleConflict = (dayIdx, sm, em) => {
    if (dayIdx == null) return null;
    const targetDate = weekDates[dayIdx];
    if (!targetDate) return null;
    for (const s of sessions || []) {
      if (s.block_kind !== "personal") continue;
      if (s.provider_id && provider?.id && s.provider_id !== provider.id) continue;
      // `getScheduleTeam` materialises one row per occurrence and
      // includes a `date` field — compare on that to honour
      // weeks_of_month projections.
      if (s.date !== targetDate) continue;
      const cs = toMin(s.start_time);
      const ce = toMin(s.end_time);
      if (sm < ce && cs < em) return s;
    }
    return null;
  };

  // FP3-065 follow-up #7 audit — Pre-warning for grade-level
  // school rules during manual drag/drop. Mirrors the wizard's
  // grade lookup: match by (school_id, normalized grade string),
  // pick the student's attendance_pattern (default "full_day"),
  // and walk that pattern's constraints[] for an overlap. Returns
  // the FIRST conflicting constraint or null.
  //
  // FP3-115 — Expand `day_of_week === -1` (Mon..Fri) so the
  // Builder's grade-rule check matches Wizard's canonical
  // interpretation. Prior code compared `c.day_of_week !== day`
  // directly, which silently ignored every "every weekday" rule
  // and caused the Builder validator to disagree with the Wizard
  // and Grade Rules Check for those same rules. The visual overlay
  // relies on the same expansion below.
  const findGradeConstraintConflict = (student, day, sm, em) => {
    if (!student || day == null) return null;
    const grade = String(student.grade ?? "").trim();
    if (!grade) return null;
    const schoolId = student.school_id;
    if (!schoolId) return null;
    const pat = normalizeAttendancePattern(student.attendance_pattern) || "full_day";
    for (const gs of gradeSchedules) {
      if (gs.school_id !== schoolId) continue;
      if (String(gs.grade ?? "").trim() !== grade) continue;
      const slot = gs.patterns?.[pat];
      if (!slot) continue;
      for (const c of slot.constraints || []) {
        const dow = typeof c.day_of_week === "number" ? c.day_of_week : -1;
        const matchesDay = dow === -1 ? day >= 0 && day <= 4 : dow === day;
        if (!matchesDay) continue;
        const cs = toMin(c.start_time);
        const ce = toMin(c.end_time);
        if (sm < ce && cs < em) {
          return { constraint: c, grade };
        }
      }
    }
    return null;
  };

  // Aggregated check for grouped drops (multi-member). Returns the
  // FIRST conflict across the group so the toast can attribute it.
  const findGroupConstraintConflict = (studentIds, day, sm, em) => {
    for (const sid of studentIds || []) {
      const hit = findStudentConstraintConflict(sid, day, sm, em);
      if (hit) return { studentId: sid, constraint: hit };
    }
    return null;
  };

  // Iter 40 — Drawer no longer auto-opens. The summary pill above the
  // canvas (which carries the issue count + a "Review →" CTA) is now
  // the intentional entry point. Auto-opening was both blocking page
  // controls and over-emphasizing validation as the primary surface
  // when the canvas should remain the focus.
  // The pill already animates a subtle pulse when fresh issues land,
  // so the user notices without a forced overlay.

  // Iter 33d Follow-up 11 — Run validation on Builder mount whenever
  // there's an active provider/period AND the schedule has at least one
  // Iter 33d Follow-up 11 — Run validation on Builder mount whenever
  // there's an active provider/period AND the schedule has at least one
  // recurring block. Hoisted into a ref-tracked guard. The actual effect
  // is mounted AFTER `mySessions` is computed (further down in this
  // component) to avoid a TDZ crash on the dependency array.
  // FP3-065 follow-up #2 — Previously this ref guarded validation
  // to "first mount only". Replaced with a session-signature
  // useEffect below so the panel auto-refreshes after a Wizard
  // apply. Keeping the ref declaration removed.

  // Drag preview state — shows a ghost at the snapped landing position before drop
  const [currentDragItem, setCurrentDragItem] = useState(null);
  const [dragPreview, setDragPreview] = useState(null);

  // Group-move state: when "Move as group" is armed via right-click, the next
  // drag of any block in this set moves the whole group as one unit. Cleared
  // after a successful group drop, on revert, or on reset.
  const [armedGroupIds, setArmedGroupIds] = useState(null); // Set<string> | null

  // Track Shift key globally and surface it as state. macOS Chrome/Safari
  // sometimes don't populate `e.shiftKey` on the native HTML5 dragstart, so
  // we mirror the modifier with window keydown/keyup listeners. Surfacing it
  // as state (not just a ref) lets us paint a visible outline on grouped
  // blocks the moment Shift is held, so users get instant feedback.
  const [shiftHeld, setShiftHeld] = useState(false);
  const shiftHeldRef = useRef(false);

  // Iter 28 — Health Panel deep-link: when ?highlight=<block_id> arrives,
  // smooth-scroll the matching session block into view so the user lands
  // directly on the issue. The pulse ring (added in BuilderBlock) keeps
  // visual focus until the user interacts. focusStudentId pre-fills the
  // builder search so commitment-level deep-links surface the right student.
  useEffect(() => {
    if (!highlightBlockId) return;
    // Wait one paint cycle so the BuilderBlock with the matching
    // data-block-ref has rendered.
    const t = setTimeout(() => {
      const el = document.querySelector(
        `[data-block-ref="${highlightBlockId}"]`
      );
      if (el && el.scrollIntoView) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }, 200);
    return () => clearTimeout(t);
  }, [highlightBlockId]);

  useEffect(() => {
    if (!focusStudentId || !allStudents?.length) return;
    const s = allStudents.find((x) => x.id === focusStudentId);
    if (s) setBuilderSearch(`${s.first_name} ${s.last_name}`);
  }, [focusStudentId, allStudents]);
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === "Shift" && !shiftHeldRef.current) {
        shiftHeldRef.current = true;
        setShiftHeld(true);
      }
    };
    const onKeyUp = (e) => {
      if (e.key === "Shift" && shiftHeldRef.current) {
        shiftHeldRef.current = false;
        setShiftHeld(false);
      }
    };
    const onBlur = () => {
      if (shiftHeldRef.current) {
        shiftHeldRef.current = false;
        setShiftHeld(false);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("keyup", onKeyUp);
    window.addEventListener("blur", onBlur);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("keyup", onKeyUp);
      window.removeEventListener("blur", onBlur);
    };
  }, []);

  useEffect(() => {
    setPendingChanges({});
    setNewSessions([]);
    setUnplacedBlocks([]);
    setSelectedStudent(null);
    setCurrentDragItem(null);
    setDragPreview(null);
    setArmedGroupIds(null);
  }, [resetSignal]);

  // Wizard draft hand-off: convert proposed_blocks into our newSessions shape
  // (one block per student, _isNew=true) so they appear as unsaved drafts
  // in the canvas. The user then reviews, can drag/edit, and explicitly Save.
  useEffect(() => {
    if (!wizardDraft || !Array.isArray(wizardDraft.blocks) || wizardDraft.blocks.length === 0) {
      return;
    }
    const ts = Date.now();
    const drafts = wizardDraft.blocks.map((b, i) => {
      const dow = b.day_of_week;
      const date = weekDates[dow] || weekDates[0];
      return {
        id: `new-wiz-${ts}-${i}`,
        student_ids: b.student_ids,
        students: b.students,
        provider_id: b.provider_id,
        session_type: b.session_type || "individual",
        date,
        day_of_week: dow,
        start_time: b.start_time,
        end_time: b.end_time,
        status: "scheduled",
        room_id: "",
        room: null,
        service_type: b.service_type || "",
        is_recurring: true,
        weeks_of_month: b.weeks_of_month || null,
        wizard_run_id: b.wizard_run_id || null,
        _isNew: true,
        _fromWizard: true,
      };
    });
    setNewSessions((prev) => [...prev, ...drafts]);
    if (onWizardDraftConsumed) onWizardDraftConsumed();
    toast.success(
      `${drafts.length} draft block${drafts.length !== 1 ? "s" : ""} added · review and Save`
    );
    // weekDates is intentionally not in deps — we only consume the draft once
    // when wizardDraft.appliedAt changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wizardDraft?.appliedAt]);

  const hasChanges = Object.keys(pendingChanges).length > 0 || newSessions.length > 0;
  const changeCount = Object.keys(pendingChanges).length + newSessions.length;

  const displaySessions = useMemo(() => {
    const moved = sessions.map((s) => {
      const c = pendingChanges[s.id];
      return c ? { ...s, ...c } : s;
    });
    return [...moved, ...newSessions];
  }, [sessions, pendingChanges, newSessions]);

  // Builder edits the recurring schedule pattern. We only show recurring-block
  // origins (synthetic 'rb__' IDs) plus locally-added new blocks. One-off session
  // rows from the `sessions` collection (past completed, quick sessions, exceptions)
  // are excluded so the Builder canvas reflects the recurring pattern itself.
  //
  // FP3-065 follow-up (Jun 1, 2026) — The wizard saves service blocks with
  // `weeks_of_month` masks (e.g. [1,2,3] for "3 of 4 weeks" monthly cadence).
  // Previously the Builder canvas filtered ANY block whose weeks_of_month
  // wasn't all-five-weeks out of view as "not typical weekly", which made
  // every wizard-saved block invisible on Builder while Calendar happily
  // rendered them. Pete's actual production bug.
  //
  // New behaviour: only rolling N-week cadence (`week_cadence > 1`, e.g.
  // every 2nd or 3rd week) is excluded from the weekly canvas because
  // those genuinely can't be represented on a single week view. Blocks
  // with `weeks_of_month` masks DO render — the block tile shows the
  // "N of 4 weeks" pill so operators see at a glance which weeks the
  // block runs. This keeps the Builder canonical with the wizard's
  // saved output AND keeps Calendar's materializer untouched.
  const isWeeklyBlock = (s) => {
    if (s.week_cadence && Number(s.week_cadence) > 1) return false;
    return true;
  };
  const mySessions = useMemo(
    () =>
      displaySessions.filter(
        (s) =>
          s.provider_id === provider?.id &&
          (s.is_recurring === true ||
            (typeof s.id === "string" && s.id.startsWith("rb__")) ||
            s._isNew === true) &&
          // Newly-added local drafts are always weekly by definition; only
          // gate cadence on persisted recurring rows.
          (s._isNew === true || isWeeklyBlock(s))
      ),
    [displaySessions, provider]
  );

  // Iter 33d Follow-up 12 — initial-mount validation effect, deferred
  // here so `mySessions` is initialized before its `.length` is read in
  // the dep array. Fires once per mount when provider+period+sessions
  // are all ready, so returning to Builder always re-surfaces unresolved
  // issues.
  // FP3-065 follow-up #2 — Auto-run validation on mount AND
  // whenever the materialized session set changes meaningfully
  // (e.g. after a Wizard apply, after a save, after navigating back
  // from another tab and the data refetched). Previously a
  // `useRef` guard locked this to "first mount only", which meant
  // stale violations from BEFORE the wizard ran kept hanging on
  // the Builder canvas — operators saw "scheduled 0 min/month" for
  // students who were already 100% covered by the wizard. The new
  // shape re-runs whenever the session-id signature flips so the
  // panel and the canvas always agree on the same data.
  const sessionsSignature = useMemo(() => {
    if (!mySessions || !mySessions.length) return "";
    // Cheap stable signature — id + start_time. Excludes
    // pending-change preview rows so an in-flight drag doesn't
    // spam the validator.
    return mySessions
      .filter((s) => !s.__pendingBlockId)
      .map((s) => `${s.id}@${s.start_time}`)
      .sort()
      .join("|");
  }, [mySessions]);
  useEffect(() => {
    if (!provider?.id || !activePeriod?.id) return;
    if (!sessionsSignature) return;
    runValidation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [provider?.id, activePeriod?.id, sessionsSignature]);

  const builderDayMap = useMemo(() => {
    const m = {};
    weekDates.forEach((d) => {
      m[d] = assignLanes(
        mySessions.filter((s) => s.date === d).sort((a, b) => a.start_time.localeCompare(b.start_time))
      );
    });
    return m;
  }, [mySessions, weekDates]);

  // Group map: blocks sharing (provider_id, date, start_time, end_time) form a
  // grouped time slot. Returns Map<sessionId, string[]> where the array holds
  // ALL member ids in the group (including itself). Singletons map to [id].
  const groupMap = useMemo(() => {
    const buckets = new Map();
    mySessions.forEach((s) => {
      const key = `${s.provider_id}|${s.date}|${s.start_time}|${s.end_time}`;
      if (!buckets.has(key)) buckets.set(key, []);
      buckets.get(key).push(s.id);
    });
    const out = new Map();
    buckets.forEach((ids) => {
      ids.forEach((id) => out.set(id, ids));
    });
    return out;
  }, [mySessions]);

  // ── FP3-115 — Grade constraint overlay (visual aid) ────────────────
  // Local state ONLY — never persisted to the account/preferences per
  // spec §14, never included in Save payloads per spec §6/§17. Cleared
  // on component unmount / route change. Auto-context (from the
  // currently-selected Students-box row) fills in only when the
  // provider has NOT explicitly picked a grade — spec §4.
  const [manualOverlayGrade, setManualOverlayGrade] = useState(""); // "" = Off
  const [manualOverlayPattern, setManualOverlayPattern] = useState(""); // "" = derive
  const [manualOverlaySchoolId, setManualOverlaySchoolId] = useState("");

  // Schools referenced by the provider's caseload + any grade rules
  // present in gradeSchedules. Used as the source of truth for the
  // (optional) school selector when a provider has grade rules for
  // more than one school. `campus` on a student row = the school's
  // canonical name.
  const overlaySchools = useMemo(() => {
    const seen = new Map();
    for (const st of allStudents || []) {
      if (st?.school_id && !seen.has(st.school_id)) {
        seen.set(st.school_id, st.campus || "");
      }
    }
    for (const gs of gradeSchedules || []) {
      if (gs?.school_id && !seen.has(gs.school_id)) {
        seen.set(gs.school_id, "");
      }
    }
    return Array.from(seen.entries()).map(([id, name]) => ({ id, name }));
  }, [allStudents, gradeSchedules]);

  // Grades that actually have at least one constraint in the currently
  // scoped school. Prevents "Grade 7" from appearing as a pickable
  // option in a K-5 school. Sort K first then numerically.
  const overlayScopedSchoolId = manualOverlaySchoolId || overlaySchools[0]?.id || "";
  const availableGradesForOverlay = useMemo(() => {
    if (!overlayScopedSchoolId) return [];
    const grades = new Set();
    for (const gs of gradeSchedules || []) {
      if (gs.school_id !== overlayScopedSchoolId) continue;
      for (const pk of ["am", "pm", "full_day"]) {
        const slot = (gs.patterns || {})[pk];
        if ((slot?.constraints || []).length > 0) {
          grades.add(String(gs.grade ?? "").trim());
          break;
        }
      }
    }
    const arr = Array.from(grades).filter(Boolean);
    arr.sort((a, b) => {
      if (a === "K") return -1;
      if (b === "K") return 1;
      const na = Number(a);
      const nb = Number(b);
      if (Number.isFinite(na) && Number.isFinite(nb)) return na - nb;
      return a.localeCompare(b);
    });
    return arr;
  }, [gradeSchedules, overlayScopedSchoolId]);

  // Patterns that have DISTINCT constraints for the selected grade.
  // If the grade only has constraints for one pattern (or all
  // patterns are identical), the pattern selector is hidden (spec §3).
  const availablePatternsForOverlay = useMemo(() => {
    if (!manualOverlayGrade || !overlayScopedSchoolId) return [];
    const gs = (gradeSchedules || []).find(
      (g) =>
        g.school_id === overlayScopedSchoolId &&
        String(g.grade ?? "").trim() === manualOverlayGrade,
    );
    if (!gs) return [];
    return ["am", "pm", "full_day"].filter(
      (pk) => ((gs.patterns || {})[pk]?.constraints || []).length > 0,
    );
  }, [manualOverlayGrade, overlayScopedSchoolId, gradeSchedules]);

  // Effective overlay context.
  //
  // Priority per FP3-115B §6:
  //   1. Selected-student context — the highest priority. When a
  //      student is clicked in the Builder Students box we surface
  //      that student's grade rules + their own Student Constraints,
  //      overriding any manual selection until the student is
  //      deselected. The manual state is preserved in
  //      `manualOverlayGrade` so §6.D "restore the previously
  //      selected manual grade overlay" happens automatically once
  //      selectedStudent flips back to null.
  //   2. Manual Grade Overlay pill selection.
  //   3. Off / no student → null (no overlays).
  const overlayContext = useMemo(() => {
    if (selectedStudent?.school_id && selectedStudent.grade != null) {
      return {
        schoolId: selectedStudent.school_id,
        grade: String(selectedStudent.grade).trim(),
        pattern:
          normalizeAttendancePattern(selectedStudent.attendance_pattern) ||
          "full_day",
        source: "student",
        studentId: selectedStudent.id,
        studentName: `${selectedStudent.first_name || ""} ${selectedStudent.last_name || ""}`.trim(),
      };
    }
    if (manualOverlayGrade) {
      let pattern = manualOverlayPattern;
      if (!pattern) {
        pattern = availablePatternsForOverlay.includes("full_day")
          ? "full_day"
          : availablePatternsForOverlay[0] || "full_day";
      }
      return {
        schoolId: overlayScopedSchoolId,
        grade: manualOverlayGrade,
        pattern,
        source: "manual",
      };
    }
    return null;
  }, [
    manualOverlayGrade,
    manualOverlayPattern,
    availablePatternsForOverlay,
    overlayScopedSchoolId,
    selectedStudent,
  ]);

  const overlayBlocks = useMemo(() => {
    if (!overlayContext) return [];
    return resolveGradeConstraintsForOverlay({
      gradeSchedules,
      schoolId: overlayContext.schoolId,
      grade: overlayContext.grade,
      pattern: overlayContext.pattern,
    });
  }, [overlayContext, gradeSchedules]);

  // FP3-115B — Student-specific constraint overlay. Rendered
  // ONLY when the current overlay context is triggered by a
  // selected student (spec §6.B/§6.D). Uses the same
  // canonical `studentConstraints` list already loaded on mount
  // and consumed by `findStudentConstraintConflict` — so the
  // visual overlay is byte-identical to actual drop-blocking
  // enforcement. Date-range applicability is evaluated against
  // the currently-displayed week (Mon..Fri).
  const studentOverlayBlocks = useMemo(() => {
    if (overlayContext?.source !== "student") return [];
    if (!overlayContext.studentId) return [];
    const weekStart = weekDates?.[0] || "";
    const weekEnd = weekDates?.[weekDates.length - 1] || "";
    return resolveStudentConstraintsForOverlay({
      studentConstraints,
      studentId: overlayContext.studentId,
      weekStart,
      weekEnd,
    });
  }, [overlayContext, studentConstraints, weekDates]);
  // ── end FP3-115 ────────────────────────────────────────────────────

  // ── FP3-115A — Bottom horizontal scrollbar ─────────────────────────
  // Adds a wide, mouse-friendly horizontal scrollbar sticky at the
  // bottom of the Builder page so desktop users with a standard
  // mouse can drag it left/right when the schedule canvas overflows
  // horizontally. Implementation:
  //   • `gridScrollRef` → the existing overflow-x-auto container that
  //     already scrolls the Monday–Friday grid. Native scrollbar is
  //     hidden via the `.builder-grid-scroll` class (trackpad/wheel
  //     still work) so only one visible scrollbar is on screen.
  //   • `phantomBarRef` → a full-workspace-width sticky bar whose
  //     scroll position mirrors the grid's. This is the visible
  //     mouse-drag surface.
  //   • The phantom bar SPANS THE FULL WORKSPACE (wider than the
  //     grid's own scroll container which sits inside the right
  //     canvas column). To make dragging the phantom thumb fully
  //     right reveal the same far-right content the grid can reach
  //     (spec §7.4 & §7.5), the inner spacer width is computed as
  //     `phantomClientWidth + gridMaxScroll` so `phantomMaxScroll
  //     === gridMaxScroll`. This lets us use IDENTITY sync
  //     (`phantom.scrollLeft <=> grid.scrollLeft`) rather than a
  //     scaled sync — simpler, avoids sub-pixel drift, and
  //     guarantees every last pixel is reachable via the phantom.
  //   • Show only when the grid actually overflows horizontally.
  //   • ResizeObserver watches both containers so the bar
  //     appears/disappears + re-sizes when the validation drawer
  //     opens/closes or the week nav changes.
  //   • Bidirectional scroll sync keeps the two containers in
  //     lockstep. A shared `syncing` flag prevents feedback loops.
  const gridScrollRef = useRef(null);
  const phantomBarRef = useRef(null);
  const [overflowState, setOverflowState] = useState({
    show: false,
    spacerWidth: 0,
  });

  useEffect(() => {
    const grid = gridScrollRef.current;
    if (!grid) return;
    const check = () => {
      const gridScrollWidth = grid.scrollWidth || 0;
      const gridClientWidth = grid.clientWidth || 0;
      const gridMax = Math.max(0, gridScrollWidth - gridClientWidth);
      const show = gridMax > 0;
      // Size the phantom's inner spacer so its own max-scroll ==
      // grid's max-scroll. This makes IDENTITY sync work end-to-end
      // (dragging the phantom fully right reveals all far-right
      // grid content per spec §7.4). Fall back to gridScrollWidth
      // if the phantom is not mounted yet — the second observer
      // tick (once the phantom mounts) will re-compute the correct
      // width.
      const phantom = phantomBarRef.current;
      const phantomClientWidth = phantom?.clientWidth || 0;
      const spacerWidth = phantomClientWidth > 0
        ? phantomClientWidth + gridMax
        : gridScrollWidth;
      setOverflowState((prev) =>
        prev.show === show && prev.spacerWidth === spacerWidth
          ? prev
          : { show, spacerWidth },
      );
    };
    check();
    const ro = new ResizeObserver(check);
    ro.observe(grid);
    if (grid.firstElementChild) ro.observe(grid.firstElementChild);
    // Observing the phantom too so the very first mount tick
    // recomputes the correct spacer width using the phantom's
    // actual on-screen clientWidth (fixes bar_max < grid_max at
    // narrow viewports where the phantom is wider than the grid).
    const phantomEl = phantomBarRef.current;
    if (phantomEl) ro.observe(phantomEl);
    window.addEventListener("resize", check);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", check);
    };
  }, [validationDrawerOpen, weekDates.length, overflowState.show]);

  useEffect(() => {
    if (!overflowState.show) return;
    const grid = gridScrollRef.current;
    const phantom = phantomBarRef.current;
    if (!grid || !phantom) return;
    let syncing = false;
    const raf = { id: 0 };
    const clearSync = () => {
      raf.id = 0;
      syncing = false;
    };
    // Identity sync in both directions. bar_max === grid_max is
    // guaranteed by the spacer-width calculation above.
    const gridToPhantom = () => {
      if (syncing) return;
      syncing = true;
      phantom.scrollLeft = grid.scrollLeft;
      raf.id = requestAnimationFrame(clearSync);
    };
    const phantomToGrid = () => {
      if (syncing) return;
      syncing = true;
      grid.scrollLeft = phantom.scrollLeft;
      raf.id = requestAnimationFrame(clearSync);
    };
    grid.addEventListener("scroll", gridToPhantom, { passive: true });
    phantom.addEventListener("scroll", phantomToGrid, { passive: true });
    // Initial sync in case grid was already scrolled.
    phantom.scrollLeft = grid.scrollLeft;
    return () => {
      if (raf.id) cancelAnimationFrame(raf.id);
      grid.removeEventListener("scroll", gridToPhantom);
      phantom.removeEventListener("scroll", phantomToGrid);
    };
  }, [overflowState.show, overflowState.spacerWidth]);
  // ── end FP3-115A ───────────────────────────────────────────────────

  // FP3-035B-2 correction — Schedule Builder is a DIRECT-SERVICE
  // surface only. Filter the panel student list to those for whom
  // the logged-in provider both (a) appears on `assigned_providers`
  // AND (b) carries `provides_direct_service=True` on the
  // team_assignments row. Evaluation-team-only assignments (e.g.
  // School Psychologist) and consult-only rows are excluded so
  // those providers don't see "service blocks" they shouldn't be
  // creating. Pre-migration students (no team_assignments yet)
  // fall back to the legacy `assigned_providers.includes` check
  // so coverage / shared-school workflows still work.
  //
  // Iter 61 correction — Also gate on EITHER a non-empty
  // viewer_service_domains list OR the presence of at least one
  // schedulable obligation (`kind != "no_direct_service"`,
  // `total_minutes > 0`). Drops legacy seed students with empty
  // services arrays that previously bled into the Builder.
  const isDirectServiceForViewer = (student) => {
    if (!student) return false;
    if (!provider?.id) return false;
    const rows = student.team_assignments || [];
    if (rows.length === 0) {
      // Legacy fallback — pre-migration record. Be permissive so
      // shared-school / coverage flows still see the row, but only
      // when the student carries a real direct-service obligation.
      const hasObligation = (student.service_obligations || []).some(
        (o) => o && o.kind !== "no_direct_service"
                 && (Number(o.total_minutes) || 0) > 0
      );
      return (student.assigned_providers || []).includes(provider.id) && hasObligation;
    }
    const myRow = rows.find((a) => a.provider_id === provider.id);
    if (!myRow) return false;
    if (myRow.assignment_status && myRow.assignment_status !== "active") return false;
    if (myRow.provides_direct_service === false) return false;
    // Require an actual schedulable obligation OR at least one
    // canonical service entry. The TEST_ITER33D legacy fixtures
    // have neither and were leaking into the Builder.
    const hasObligation = (student.service_obligations || []).some(
      (o) => o && o.kind !== "no_direct_service"
               && (Number(o.total_minutes) || 0) > 0
    );
    const hasServices = (student.services || []).length > 0
      || (student.service_areas || []).length > 0;
    return hasObligation || hasServices;
  };
  const panelStudents = useMemo(() => {
    let list = allStudents.filter(isDirectServiceForViewer);
    // FP3-106D — Builder is a DIRECT-SERVICE workspace. Exclude
    // students already covered by a supervised assistant so the
    // supervising provider's Builder list reflects who THEY are
    // actually scheduling, not the broader team. The backend
    // (routes/students.py::_hydrate_assistant_covered) already
    // stamps `assistant_covered` from the perspective of the
    // caseload owner (`GET /students?provider_id=<caller>`); we
    // just consume the flag here. Assistants themselves never see
    // the flag (backend skips stamping for assistant callers), so
    // an assistant's Builder list is naturally untouched.
    list = list.filter((s) => !s.assistant_covered);
    if (builderSearch) {
      const q = builderSearch.toLowerCase();
      list = list.filter((s) => `${s.first_name} ${s.last_name}`.toLowerCase().includes(q));
    }
    return list;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allStudents, provider, builderSearch]);

  // Block counts per student (placed + unplaced) for the selected student panel.
  const studentBlockCount = useMemo(() => {
    if (!selectedStudent) return 0;
    // Iter 32 — count DISTINCT recurring blocks, not materialized weekday
    // sessions. `mySessions` expands each weekly recurring block into up
    // to 5 occurrences (Mon–Fri), which was inflating the count
    // (e.g. Noah's 2 real blocks were reporting as 8). Dedupe on
    // `_recurring_block_id`; fall back to session.id when absent so ad-hoc
    // one-off sessions still contribute.
    const seen = new Set();
    mySessions.forEach((s) => {
      if (!s.student_ids?.includes(selectedStudent.id)) return;
      seen.add(s._recurring_block_id || s.id);
    });
    const tray = unplacedBlocks.filter((b) => b.student?.id === selectedStudent.id).length;
    return seen.size + tray;
  }, [selectedStudent, mySessions, unplacedBlocks]);

  // FP3-078D-2 — Removed `computeWeeksOfMonthForStudent` helper.
  // It used to auto-derive `weeks_of_month=[1..N]` from a student's
  // monthly cadence and stamp it onto manually-added Builder blocks,
  // which silently excluded week-5 days from the canvas after Save.
  // Manual placements now default to `weeks_of_month=null` ("every
  // week") per the Pydantic model contract. Wizard cadence still
  // writes explicit masks server-side.

  // FP3-035B-2 correction — Helper that returns the labeled service
  // list narrowed to the LOGGED-IN provider's assigned domains for
  // the student. Used by the service-header line in the creator
  // pane AND by addBlock() to default the new block's service_type
  // to a relevant value. Falls back to the student's full service
  // list when the row carries no viewer hint (admin viewer, legacy).
  const viewerServicesForStudent = (student) => {
    if (!student) return [];
    const canonical = student.services || [];
    const viewerDomains = Array.isArray(student.viewer_service_domains)
      ? student.viewer_service_domains
      : null;
    if (canonical.length) {
      const labeled = canonical.map((s) => ({
        key: s.key,
        label: s.label || s.key,
        domain: s.domain || "",
      }));
      if (viewerDomains && viewerDomains.length) {
        const wanted = new Set(viewerDomains);
        return labeled
          .filter((s) => !s.domain || wanted.has(s.domain))
          .map((s) => s.label);
      }
      return labeled.map((s) => s.label);
    }
    return student.service_areas || [];
  };

  const addBlock = (dur) => {
    if (!selectedStudent) return;
    // FP3-035B-2 correction — Default the block's service_type to a
    // domain the LOGGED-IN provider is actually responsible for on
    // this student. Falls back to the student's first service_area
    // when the viewer-specific list is unavailable (pre-migration
    // students, admin viewer).
    const viewerLabels = viewerServicesForStudent(selectedStudent);
    const defaultService = (viewerLabels[0]
      || selectedStudent.service_areas?.[0]
      || "language");
    // FP3-078D-2 — Manual Builder placements MUST default to
    // `weeks_of_month=null` ("every week of every month" per the
    // Pydantic contract) so they appear on the exact week the user
    // dropped them on. Previously this path auto-inherited the
    // student's monthly cadence as `[1..N]`, which silently excluded
    // week-5 days (Jul 29-31, Aug 30-31, etc.) — the placed block
    // would vanish from the canvas right after Save and reappear the
    // following week. The Wizard still writes explicit
    // `weeks_of_month` masks for monthly students; only manual
    // Builder placements default to "every week" now. If a future UI
    // adds an explicit cadence picker for manual placements, set
    // `weeks_of_month` here from that user choice (not auto-derived
    // from the student's service obligation).
    setUnplacedBlocks((prev) => [
      ...prev,
      {
        id: `up-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
        student: selectedStudent,
        duration: dur,
        service_type: defaultService,
        block_kind: "service",
        weeks_of_month: null,
      },
    ]);
  };

  // Iter 24 — provider-owned non-student blocks (lunch / prep / duties / testing / meetings / other)
  const addPersonalBlock = (kind, dur, label) => {
    setUnplacedBlocks((prev) => [
      ...prev,
      {
        id: `up-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
        student: null,
        duration: dur,
        block_kind: "personal",
        personal_kind: kind,
        personal_label: label || kind.charAt(0).toUpperCase() + kind.slice(1),
        service_type: "",
      },
    ]);
  };

  const removeUnplaced = (id) => setUnplacedBlocks((prev) => prev.filter((b) => b.id !== id));

  // Compute snapped start time from clientX within a dropzone rect, given a duration.
  const computeSnappedStart = (clientX, rect, dur) => {
    const rawMin = ((clientX - rect.left) / rect.width) * TOTAL_MIN + TIME_START;
    const snapped = Math.round(rawMin / SNAP) * SNAP;
    return Math.max(TIME_START, Math.min(snapped, TIME_END - dur));
  };

  const handleDragOver = (e, dayIdx) => {
    e.preventDefault();
    // Force "move" semantics. macOS Chrome flips dropEffect to "link" while
    // Shift is held, and since effectAllowed wouldn't otherwise include link,
    // the drop is silently rejected. Setting dropEffect="move" here
    // overrides the modifier-key coercion so Shift+drag actually completes.
    if (e.dataTransfer) e.dataTransfer.dropEffect = "move";
    if (!currentDragItem) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const dur = currentDragItem.dur;
    const startMin = computeSnappedStart(e.clientX, rect, dur);
    // FP3-065 follow-up #7 audit — Live student-constraint check
    // during drag-over so the hover hint can preview the block in
    // a warning state. The drop guard below uses the same check
    // to BLOCK the drop with a toast.
    const draggedStudentIds = [
      currentDragItem.studentId,
      ...(Array.isArray(currentDragItem.studentIds) ? currentDragItem.studentIds : []),
      ...(currentDragItem.memberIds || []),
    ].filter(Boolean);
    const conflict = findGroupConstraintConflict(
      draggedStudentIds, dayIdx, startMin, startMin + dur
    );
    const providerRuleConflict = findProviderRuleConflict(
      dayIdx, startMin, startMin + dur
    );
    // FP3-065 follow-up #7 audit — Grade-level pre-warning. Use the
    // FULL student row from the drag payload (already enriched by
    // the sidebar onDragStart with grade / school_id /
    // attendance_pattern). When multiple members are dragged
    // together, the first conflicting member wins the message.
    const draggedStudentRows = [
      currentDragItem.student,
      ...(Array.isArray(currentDragItem.students) ? currentDragItem.students : []),
    ].filter(Boolean);
    let gradeConflict = null;
    for (const st of draggedStudentRows) {
      const hit = findGradeConstraintConflict(st, dayIdx, startMin, startMin + dur);
      if (hit) {
        gradeConflict = { student: st, ...hit };
        break;
      }
    }
    let hoverConflict = null;
    if (conflict) {
      hoverConflict = {
        kind: "student",
        studentName: (currentDragItem.student
          ? `${currentDragItem.student.first_name} ${currentDragItem.student.last_name}`
          : "Student"),
        label: conflict.constraint.label || conflict.constraint.kind || "student constraint",
      };
    } else if (gradeConflict) {
      hoverConflict = {
        kind: "grade",
        studentName: null,
        label: `Grade ${gradeConflict.grade} · ${gradeConflict.constraint.label || gradeConflict.constraint.kind || "school rule"}`,
      };
    } else if (providerRuleConflict) {
      hoverConflict = {
        kind: "provider",
        studentName: null,
        label: providerRuleConflict.personal_label
          || providerRuleConflict.personal_kind
          || "Provider rule",
      };
    }
    setDragPreview({
      dayIdx,
      startMin,
      dur,
      conflict: hoverConflict,
    });
  };

  const handleDragLeave = () => {
    // Only clear if we're leaving the entire grid; small flicker is acceptable
    setDragPreview(null);
  };

  // Reusable group-move applier — used by both the HTML5 drop handler
  // (right-click → armed group case) and the custom mouse-driven Shift+drag
  // path (which bypasses HTML5 D&D because macOS Chrome suppresses dragstart
  // when Shift is held).
  const applyGroupMove = (memberIds, dayIdx, startTime, endTime) => {
    if (!Array.isArray(memberIds) || memberIds.length < 2) return;
    const newDate = weekDates[dayIdx];
    if (!newDate) return;
    const groupSet = new Set(memberIds);
    setNewSessions((prev) =>
      prev.map((s) =>
        groupSet.has(s.id)
          ? { ...s, start_time: startTime, end_time: endTime, date: newDate, day_of_week: dayIdx }
          : s
      )
    );
    setPendingChanges((p) => {
      const next = { ...p };
      memberIds.forEach((id) => {
        if (typeof id === "string" && id.startsWith("new-")) return; // handled above
        next[id] = {
          start_time: startTime,
          end_time: endTime,
          date: newDate,
          day_of_week: dayIdx,
        };
      });
      return next;
    });
    setArmedGroupIds(null);
  };

  const handleDrop = (e, dayIdx) => {
    e.preventDefault();
    let data;
    try {
      data = JSON.parse(e.dataTransfer.getData("application/json"));
    } catch {
      setDragPreview(null);
      setCurrentDragItem(null);
      return;
    }
    const rect = e.currentTarget.getBoundingClientRect();
    const startMin = computeSnappedStart(e.clientX, rect, data.dur);
    const ns = toTime(startMin);
    const ne = toTime(startMin + data.dur);
    const newDate = weekDates[dayIdx];

    // FP3-065 follow-up #7 audit — Pre-warning + block on
    // student-constraint conflicts. We check the dragged student
    // (and any grouped members for Shift-drag) against the local
    // `studentConstraints` snapshot. If a conflict exists, we
    // surface a toast naming the student + constraint label and
    // ABORT the drop. The backend validator still catches anything
    // that slips through (e.g. constraints added during this
    // session), so this is a UX improvement, not a security gate.
    const draggedStudentIds = [
      data.studentId,
      ...(Array.isArray(data.studentIds) ? data.studentIds : []),
      ...(Array.isArray(data.memberIds) ? data.memberIds : []),
    ].filter(Boolean);
    const conflict = findGroupConstraintConflict(
      draggedStudentIds, dayIdx, startMin, startMin + data.dur
    );
    if (conflict) {
      const studentRow = (data.student && data.student.id === conflict.studentId)
        ? data.student
        : null;
      const studentName = studentRow
        ? `${studentRow.first_name} ${studentRow.last_name}`
        : "Student";
      const label = conflict.constraint.label
        || conflict.constraint.kind
        || "student constraint";
      toast.error(
        `${studentName} has "${label}" at this time. Drop blocked.`
      );
      setDragPreview(null);
      setCurrentDragItem(null);
      return;
    }
    // FP3-065 follow-up #7 audit — Grade-level pre-warning. Block
    // service-block drops onto a window blocked by the dragged
    // student's grade-level school rule. Skip when dragging a
    // personal block (provider may legitimately reshape rules).
    if (data.block_kind !== "personal") {
      const draggedRows = [
        data.student,
        ...(Array.isArray(data.students) ? data.students : []),
      ].filter(Boolean);
      for (const st of draggedRows) {
        const gHit = findGradeConstraintConflict(
          st, dayIdx, startMin, startMin + data.dur
        );
        if (gHit) {
          const label = gHit.constraint.label
            || gHit.constraint.kind
            || "school rule";
          toast.error(
            `Grade ${gHit.grade} constraint conflict: "${label}". Drop blocked.`
          );
          setDragPreview(null);
          setCurrentDragItem(null);
          return;
        }
      }
    }
    // FP3-065 follow-up #7 audit — Provider-rule pre-warning.
    // Block service-block drops onto a personal Provider Rule slot
    // with a toast carrying the rule label. Skip the guard when the
    // dragged item IS a personal block (the operator's intentionally
    // editing the rule itself).
    if (data.block_kind !== "personal") {
      const providerHit = findProviderRuleConflict(
        dayIdx, startMin, startMin + data.dur
      );
      if (providerHit) {
        const label = providerHit.personal_label
          || providerHit.personal_kind
          || "Provider rule";
        toast.error(
          `Blocked by provider rule: "${label}". Drop blocked.`
        );
        setDragPreview(null);
        setCurrentDragItem(null);
        return;
      }
    }

    // Re-evaluate Shift state AT DROP TIME (most reliable place to check
    // modifier keys; macOS Chrome can suppress shiftKey on dragstart).
    const shiftAtDrop = e.shiftKey || !!shiftHeldRef.current;
    // Final group-move decision — explicit groupIds (right-click armed) OR
    // Shift held at drop time on a multi-member source.
    const explicitGroupIds = Array.isArray(data.groupIds) ? data.groupIds : null;
    const inferredGroupIds =
      shiftAtDrop && Array.isArray(data.memberIds) && data.memberIds.length > 1
        ? data.memberIds
        : null;
    const finalGroupIds = explicitGroupIds || inferredGroupIds;

    if (data.type === "new") {
      const sess = {
        id: `new-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
        student_ids: data.studentId ? [data.studentId] : [],
        students: data.student ? [data.student] : [],
        provider_id: provider.id,
        session_type: data.block_kind === "personal" ? "personal" : "individual",
        date: newDate,
        day_of_week: dayIdx, // 0=Mon..4=Fri — source of truth for save
        start_time: ns,
        end_time: ne,
        status: "scheduled",
        room_id: "",
        room: null,
        service_type: data.service_type || "",
        is_recurring: true,
        _isNew: true,
        // Iter 24 — provider-owned non-student blocks
        block_kind: data.block_kind || "service",
        personal_kind: data.personal_kind || null,
        personal_label: data.personal_label || "",
        // FP3-065 follow-up #4 — Persist the monthly cadence
        // inherited from the unplaced block. handleSave forwards
        // this to `api.createRecurringBlock`, so the validator
        // counts the same monthly minutes the wizard would have.
        weeks_of_month: data.weeks_of_month || null,
      };
      setNewSessions((prev) => [...prev, sess]);
      setUnplacedBlocks((prev) => prev.filter((b) => b.id !== data.blockId));
    } else if (Array.isArray(finalGroupIds) && finalGroupIds.length > 1) {
      applyGroupMove(finalGroupIds, dayIdx, ns, ne);
    } else if (data.isNew) {
      setNewSessions((prev) =>
        prev.map((s) =>
          s.id === data.id
            ? { ...s, start_time: ns, end_time: ne, date: newDate, day_of_week: dayIdx }
            : s
        )
      );
    } else {
      // FP3-121A — Occurrence-level move detection.
      //
      // When the dragged block is a materialized session from a
      // multi-day recurring block (parent's `_days_of_week` has 2+
      // days), a plain "update the parent block" would move
      // Monday AND Wednesday AND Friday — not what the provider
      // intended when they dragged just the Wednesday pill.
      //
      // Solution: tag the pendingChanges entry with `_split` so
      // `handleSave` routes it through `builderSave.splits`, which
      // atomically peels the moved day off the parent and creates
      // a fresh single-day recurring block at the new time.
      const src = sessions.find((s) => s.id === data.id);
      const daysMask = Array.isArray(src?._days_of_week) ? src._days_of_week : null;
      const isMultiDay = daysMask && daysMask.length > 1;
      // Original day-of-week is the one being peeled off.
      // Resolve it from the source session so a stale drag
      // payload can't accidentally detach the WRONG day.
      let detachDay = null;
      if (typeof src?._day_of_week === "number") {
        detachDay = src._day_of_week;
      } else if (src?.date && Array.isArray(weekDates)) {
        const idx = weekDates.indexOf(src.date);
        if (idx >= 0 && idx <= 4) detachDay = idx;
      }
      const nextEntry = {
        start_time: ns,
        end_time: ne,
        date: newDate,
        day_of_week: dayIdx,
      };
      if (isMultiDay && detachDay !== null && String(data.id).startsWith("rb__")) {
        nextEntry._split = {
          source_block_id: String(data.id).split("__")[1],
          detach_day: detachDay,
        };
      }
      setPendingChanges((p) => ({
        ...p,
        [data.id]: nextEntry,
      }));
    }
    setDragPreview(null);
    setCurrentDragItem(null);
  };

  // Resize callback for placed blocks: updates end_time only.
  const handleResize = (sessionId, isNew, newEndTime) => {
    if (isNew) {
      setNewSessions((prev) => prev.map((s) => (s.id === sessionId ? { ...s, end_time: newEndTime } : s)));
    } else {
      const original = sessions.find((s) => s.id === sessionId);
      if (!original) return;
      setPendingChanges((p) => ({
        ...p,
        [sessionId]: { ...(p[sessionId] || { start_time: original.start_time, date: original.date }), end_time: newEndTime },
      }));
    }
  };

  // Delete a block. For locally-added new blocks, just remove from state.
  // For real recurring blocks, open the in-app confirm dialog (Iter 33d
  // Follow-up 10 — replaces the native window.confirm popup).
  const [pendingDeleteBlock, setPendingDeleteBlock] = useState(null);
  const handleDelete = async (session) => {
    if (session._isNew) {
      setNewSessions((prev) => prev.filter((s) => s.id !== session.id));
      return;
    }
    // Iter 42 — The canvas surfaces three distinct session shapes.
    // Each needs a DIFFERENT delete path; mixing them up caused the
    // "block removed but still visible" regression.
    //   1. Synthetic materialized recurring-block instance:
    //        id = "rb__<uuid>__<YYYY-MM-DD>"
    //      → delete the recurring_block (cascades materialized rows).
    //   2. Persisted session row that was materialized off a recurring
    //      block (happens after a Live Session was launched):
    //        id = "<uuid>", _recurring_block_id = "<uuid>"
    //      → delete the `sessions` row only; KEEP the parent block so
    //        future weeks keep materializing.
    //   3. Pure one-off session (quick session, manual create):
    //        id = "<uuid>", no _recurring_block_id
    //      → delete the `sessions` row only.
    // We stamp a `_kind` discriminator on the pending session so the
    // confirm handler knows which API to hit without re-parsing.
    if (typeof session.id === "string" && session.id.startsWith("rb__")) {
      setPendingDeleteBlock({ ...session, _deleteKind: "recurring_block" });
      return;
    }
    if (typeof session.id === "string") {
      // Any real UUID row (with or without a recurring-block ref) →
      // delete just this one session.
      setPendingDeleteBlock({ ...session, _deleteKind: "session_row" });
      return;
    }
    toast.error("Couldn't identify the block to remove — try refreshing the page");
  };

  const confirmDeleteBlock = async (opts = {}) => {
    const session = pendingDeleteBlock;
    if (!session) return;
    // FP3-121A — `scope` is passed by the confirm button:
    //   "occurrence" — remove only the clicked weekday from a
    //                  multi-day parent (via builder_save.splits).
    //   "series"     — remove the entire recurring block.
    //   "session_row"— remove just the persisted `sessions` row.
    // Legacy callers with no opts fall back to the FP3-121 behavior
    // (recurring-block delete for rb__ ids, session-row delete for
    // pure UUIDs) so nothing outside the dialog changes.
    const kind = session._deleteKind || "recurring_block";
    const scope = opts.scope || (kind === "session_row" ? "session_row" : "series");
    setPendingDeleteBlock(null);
    let removed = false;
    const cleanupKey = session.id;
    try {
      if (scope === "occurrence") {
        // Compute the day being peeled off from the session's date.
        // Materialized sessions carry `date`; fall back to
        // `_day_of_week` for legacy shapes.
        let detachDay = null;
        if (session.date) {
          const jsDow = new Date(session.date + "T12:00:00").getDay();
          const idx = jsDow === 0 ? 6 : jsDow - 1;
          if (idx >= 0 && idx <= 4) detachDay = idx;
        }
        if (detachDay === null && typeof session._day_of_week === "number") {
          detachDay = session._day_of_week;
        }
        if (detachDay === null) {
          toast.error("Couldn't determine which day to remove — try refreshing");
          return;
        }
        const parts = String(session.id).split("__");
        const blockId = parts[0] === "rb" && parts.length >= 2 ? parts[1] : session.id;
        // FP3-121A — Atomic occurrence-delete via builder_save.splits.
        // No new_start_time/new_end_time → the day is dropped from
        // the parent's days_of_week (or the parent is deleted if
        // this was its only remaining day). If preflight rejects,
        // ZERO writes are applied.
        // FP3-124 §9 — Occurrence-delete is effective-date forward.
        const _now = new Date();
        const _eff = `${_now.getFullYear()}-${String(_now.getMonth() + 1).padStart(2, "0")}-${String(_now.getDate()).padStart(2, "0")}`;
        await api.builderSave({
          schedule_period_id: activePeriod.id,
          provider_id: provider.id,
          updates: [],
          creates: [],
          deletes: [],
          splits: [{
            client_id: `occdel-${blockId}-${detachDay}-${Date.now()}`,
            source_block_id: blockId,
            detach_day: detachDay,
          }],
          effective_date: _eff,
        });
      } else if (scope === "series") {
        const parts = String(session.id).split("__");
        const blockId = parts[0] === "rb" && parts.length >= 2 ? parts[1] : session.id;
        setValidationViolations((prev) => prev.filter((v) => v.block_id !== blockId));
        // FP3-124 §8 — Whole-series delete is now effective-date
        // forward: historical dates BEFORE today keep the block
        // visible, and its logged sessions survive.
        const _now = new Date();
        const _eff = `${_now.getFullYear()}-${String(_now.getMonth() + 1).padStart(2, "0")}-${String(_now.getDate()).padStart(2, "0")}`;
        await api.deleteRecurringBlock(blockId, _eff);
      } else {
        // scope === "session_row"
        await api.deleteSession(session.id);
      }
      removed = true;
    } catch (err) {
      if (err?.response?.status === 404) {
        removed = true;
      } else {
        toast.error(
          err?.response?.data?.detail?.message
          || "Failed to remove session"
        );
        runValidation();
        return;
      }
    }
    if (removed) {
      setPendingChanges((p) => {
        const next = { ...p };
        delete next[cleanupKey];
        return next;
      });
      toast.success(
        scope === "occurrence"
          ? "Day removed from series"
          : scope === "series"
          ? "Block removed"
          : "Session removed"
      );
      try {
        if (onSaved) await onSaved();
      } catch { /* refetch errors are non-fatal */ }
      runValidation();
    }
  };

  // FP3-113 / FP3-113A — Export the current Builder grid state as a
  // simple CSV. Frontend-only: reads directly from `mySessions`, which
  // is the exact effective collection the canvas renders (saved
  // recurring blocks + pending moves + newly-placed drafts; deletes
  // are already gone from the parent `sessions` prop). Does NOT hit
  // the backend and does NOT modify any schedule data.
  //
  // FP3-113A root cause: the previous implementation required each
  // session to carry a numeric `day_of_week`. That's true for local
  // drafts (set by `handleDrop`) and for the recurring_blocks table
  // itself, but the materialized sessions returned by
  // `_materialize_sessions` (the exact rows the canvas paints) DO
  // NOT carry `day_of_week` — they carry a concrete `date` like
  // "2026-08-17" instead. Every real block was silently skipped and
  // the CSV came out empty. The fix normalises the weekday index
  // from either field via `resolveDayIdx`.
  const handleExportGridCsv = () => {
    // Weekday resolver — accepts either shape used at runtime.
    // Order:
    //   1. numeric `day_of_week` 0..4                          (local drafts, pendingChange overlays)
    //   2. `weekDates.indexOf(date)` for the CURRENT week      (materialized sessions on this Mon-Fri)
    //   3. Parse YYYY-MM-DD and map JS getDay() → 0..4         (fallback for any stray shape)
    const resolveDayIdx = (s) => {
      if (typeof s.day_of_week === "number" && s.day_of_week >= 0 && s.day_of_week <= 4) {
        return s.day_of_week;
      }
      if (s.date && Array.isArray(weekDates)) {
        const idx = weekDates.indexOf(s.date);
        if (idx >= 0 && idx <= 4) return idx;
      }
      if (typeof s.date === "string" && /^\d{4}-\d{2}-\d{2}$/.test(s.date)) {
        const [y, mo, d] = s.date.split("-").map(Number);
        const jsDow = new Date(Date.UTC(y, mo - 1, d)).getUTCDay(); // 0=Sun..6=Sat
        const mondayIdx = jsDow === 0 ? -1 : jsDow - 1; // Sun→-1, Mon→0, ..., Fri→4, Sat→5
        if (mondayIdx >= 0 && mondayIdx <= 4) return mondayIdx;
      }
      return -1;
    };

    // Student-name resolver — hydrated block objects first, then id
    // lookup against the loaded Builder student list. Never returns
    // an internal id.
    const nameById = new Map();
    for (const st of allStudents || []) {
      if (!st?.id) continue;
      const nm = `${st.first_name || ""} ${st.last_name || ""}`.trim();
      if (nm) nameById.set(st.id, nm);
    }
    const displayNameForStudent = (st, id) => {
      if (st) {
        const fn = st.first_name || st.firstName || "";
        const ln = st.last_name || st.lastName || "";
        const nm = `${fn} ${ln}`.trim();
        if (nm) return nm;
      }
      if (id && nameById.has(id)) return nameById.get(id);
      return "";
    };

    // Build 5-minute rows across the Builder's displayed daily range
    // ([TIME_START, TIME_END)).
    const rows = [];
    for (let m = TIME_START; m < TIME_END; m += SNAP) {
      rows.push({ min: m, cells: ["", "", "", "", ""] });
    }
    const rowIndexFor = (m) => Math.round((m - TIME_START) / SNAP);

    // Consolidate blocks that share (day, start, end, kind, personal_label).
    // Service blocks with the same key form one scheduled group — a
    // single cell entry regardless of how many student rows exist.
    const buckets = new Map();
    let exportableCount = 0;
    let skipped = 0;
    for (const s of mySessions) {
      if (!s || !s.start_time || !s.end_time) { skipped += 1; continue; }
      const dayIdx = resolveDayIdx(s);
      if (dayIdx < 0) { skipped += 1; continue; }
      const startMin = toMin(s.start_time);
      if (startMin < TIME_START || startMin >= TIME_END) { skipped += 1; continue; }
      const kind = s.block_kind === "personal" ? "personal" : "service";
      const label =
        kind === "personal"
          ? (s.personal_label || s.personal_kind || "Personal")
          : "";
      const key = `${dayIdx}|${s.start_time}|${s.end_time}|${kind}|${label}`;
      if (!buckets.has(key)) {
        buckets.set(key, {
          day: dayIdx,
          start: s.start_time,
          end: s.end_time,
          kind,
          label,
          names: [],
          seenIds: new Set(),
        });
      }
      exportableCount += 1;
      if (kind === "service") {
        const b = buckets.get(key);
        const roster = Array.isArray(s.students) ? s.students : [];
        const ids = Array.isArray(s.student_ids) ? s.student_ids : [];
        // Hydrated first — one loop per index so an id with a
        // matching hydrated object gets its name, then any leftover
        // ids fall back to the roster lookup.
        const takenIds = new Set();
        for (const st of roster) {
          const id = st?.id;
          if (!id || b.seenIds.has(id)) continue;
          const nm = displayNameForStudent(st, id);
          if (!nm) continue;
          b.seenIds.add(id);
          takenIds.add(id);
          b.names.push(nm);
        }
        for (const id of ids) {
          if (!id || b.seenIds.has(id) || takenIds.has(id)) continue;
          const nm = displayNameForStudent(null, id);
          if (!nm) continue;
          b.seenIds.add(id);
          b.names.push(nm);
        }
      }
    }

    // Safety fallback — if the canvas has blocks but nothing is
    // exportable, show an error and abort the download. Prevents
    // the "silent blank CSV" failure mode from FP3-113A.
    if (mySessions.length > 0 && buckets.size === 0) {
      console.error(
        "[FP3-113A] Grid CSV export produced zero exportable events " +
        `despite ${mySessions.length} visible blocks — skipped ${skipped}.`,
        { sample: mySessions[0] }
      );
      toast.error(
        "Could not export the current schedule because the visible blocks could not be read. No file was created."
      );
      return;
    }

    // Place each consolidated block into its start-time row / weekday
    // column. Format: "Student A; Student B — 30 min" for service
    // groups, "Personal Label — 30 min" for personal blocks. Multiple
    // distinct entries in the same cell are joined with " | ".
    for (const b of buckets.values()) {
      const startMin = toMin(b.start);
      const endMin = toMin(b.end);
      const duration = Math.max(0, endMin - startMin);
      const idx = rowIndexFor(startMin);
      if (idx < 0 || idx >= rows.length) continue;
      let entry;
      if (b.kind === "personal") {
        entry = `${b.label} — ${duration} min`;
      } else {
        const firstNames = b.names
          .map((nm) => (nm.split(/\s+/)[0] || nm).trim())
          .filter(Boolean);
        const roster = firstNames.length ? firstNames.join("; ") : "Group";
        entry = `${roster} — ${duration} min`;
      }
      const existing = rows[idx].cells[b.day];
      rows[idx].cells[b.day] = existing ? `${existing} | ${entry}` : entry;
    }

    // CSV serialize — escape commas, quotes, and newlines per RFC 4180.
    const csvEscape = (v) => {
      if (v == null) return "";
      const s = String(v);
      if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
      return s;
    };
    const fmtTimeLabel = (m) => {
      const h24 = Math.floor(m / 60);
      const mm = m % 60;
      const isPM = h24 >= 12;
      const h12 = h24 % 12 || 12;
      return `${h12}:${String(mm).padStart(2, "0")}:00 ${isPM ? "PM" : "AM"}`;
    };
    const lines = [["Time", ...DAYS].join(",")];
    for (const r of rows) {
      lines.push([fmtTimeLabel(r.min), ...r.cells.map(csvEscape)].join(","));
    }
    const csv = lines.join("\n") + "\n";

    // Trigger a client-side download. Filename uses local date.
    const today = new Date();
    const y = today.getFullYear();
    const mo = String(today.getMonth() + 1).padStart(2, "0");
    const d = String(today.getDate()).padStart(2, "0");
    const filename = `schedule-grid-${y}-${mo}-${d}.csv`;
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 0);
    // Optional visibility for debugging in real-world usage.
    console.debug(
      `[FP3-113] Exported ${filename} · visible=${mySessions.length} · exportable_events=${exportableCount} · groups=${buckets.size} · skipped=${skipped}`
    );
    toast.success(`Exported ${filename}`);
  };

  // FP3-109 — Atomic Save. Instead of looping through each pending
  // change and firing sequential PUT/POST calls (which risked partial
  // saves + generic 409s), we send the entire changeset to
  // `POST /schedule/builder/save`. The backend preflights ALL blocks
  // against student constraints and writes NOTHING if any conflict
  // exists. Failures now surface a structured error the FE renders as
  // an actionable message per offending block.
  const handleSave = async () => {
    if (!activePeriod?.id) {
      toast.error("No active schedule period");
      return;
    }
    setSaving(true);
    setSaveConflicts([]);
    try {
      // Split pending changes into recurring-block updates vs one-off
      // session updates.
      //
      // FP3-121 — Identity discipline. Every visible Builder block is
      // treated as an independent entity. When the user drags a
      // saved block:
      //   • Synthetic materialized session (id starts with "rb__")
      //     → we update the parent recurring_block via builderSave.
      //   • Persisted session row (id = UUID) with a
      //     `_recurring_block_id`  →  we update BOTH the session row
      //     (so this-week's history stays correct) AND the parent
      //     recurring_block (so future weeks don't re-materialize
      //     the OLD location, which caused the "moved block but old
      //     one is still there after Save" duplication bug).
      //   • Persisted session row with NO recurring_block_id
      //     → truly one-off; update just the session row.
      //
      // Duplicate protection: dedupe recurring-block updates by
      // block_id so a group move that touches multiple session
      // shapes for the SAME block still emits exactly ONE update.
      const updates = [];
      const oneOffs = [];
      // FP3-121A — Occurrence-level splits. Each entry maps to
      // exactly one BuilderSaveSplit on the wire: parent block loses
      // one day, a new single-day child block is created at the
      // provider's chosen time. All rides the same atomic preflight.
      const splits = [];
      const updateByBlockId = new Map();
      const pushBlockUpdate = (blockId, changes) => {
        if (!blockId) return;
        const patch = { block_id: blockId };
        if (changes.start_time) patch.start_time = changes.start_time;
        if (changes.end_time) patch.end_time = changes.end_time;
        if (changes.day_of_week !== undefined) patch.day_of_week = changes.day_of_week;
        if (updateByBlockId.has(blockId)) {
          Object.assign(updateByBlockId.get(blockId), patch);
        } else {
          updateByBlockId.set(blockId, patch);
          updates.push(patch);
        }
      };
      for (const [id, changes] of Object.entries(pendingChanges)) {
        // FP3-121A — Occurrence-level move takes priority over the
        // whole-block update path.
        if (changes._split) {
          splits.push({
            // Deterministic child id so replaying the save (e.g. after
            // a transient 500) doesn't double-insert the moved
            // occurrence.
            client_id: `split-${id}-${changes.day_of_week}-${changes.start_time}`,
            source_block_id: changes._split.source_block_id,
            detach_day: changes._split.detach_day,
            new_day_of_week: changes.day_of_week,
            new_start_time: changes.start_time,
            new_end_time: changes.end_time,
          });
          continue;
        }
        if (id.startsWith("rb__")) {
          const blockId = id.split("__")[1];
          pushBlockUpdate(blockId, changes);
        } else {
          const src = (sessions || []).find((s) => s.id === id);
          const parentBlockId = src?._recurring_block_id;
          if (parentBlockId) {
            pushBlockUpdate(parentBlockId, changes);
          }
          oneOffs.push({ id, changes });
        }
      }
      const creates = newSessions.map((ns) => ({
        // Deterministic client-side id so a replayed Save (network
        // retry) never double-inserts the same block.
        client_id: ns._clientId || ns.id || `bs_${crypto.randomUUID?.() || Math.random().toString(36).slice(2)}`,
        schedule_period_id: activePeriod.id,
        provider_id: ns.provider_id,
        student_ids: ns.student_ids,
        day_of_week: ns.day_of_week,
        start_time: ns.start_time,
        end_time: ns.end_time,
        session_type: ns.session_type,
        service_type: ns.service_type || "",
        room_id: ns.room_id || "",
        block_kind: ns.block_kind || "service",
        personal_kind: ns.personal_kind || null,
        personal_label: ns.personal_label || "",
        weeks_of_month: ns.weeks_of_month || null,
        wizard_run_id: ns.wizard_run_id || null,
        block_source: ns._fromWizard ? "wizard" : "builder",
      }));
      // Atomic write for recurring blocks first — must pass preflight.
      // FP3-121A — splits ride in the same atomic transaction so
      // an occurrence move that would leave the schedule invalid
      // is rejected wholesale (no half-detached parent + no orphan
      // child block).
      if (updates.length || creates.length || splits.length) {
        // FP3-124 §5 — Effective-date is the provider's LOCAL
        // calendar date at Save time. Any occurrence STRICTLY
        // BEFORE this date renders with the historical schedule
        // (block versioning); any occurrence on/after uses the new
        // shape. Backend defaults to server UTC date when absent,
        // so this is a strict correctness improvement, not a hard
        // requirement.
        const now = new Date();
        const effective_date = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
        await api.builderSave({
          schedule_period_id: activePeriod.id,
          provider_id: provider.id,
          updates,
          creates,
          deletes: [],
          splits,
          effective_date,
        });
      }
      // One-off session edits (rare) still go per-row; they don't
      // affect the recurring canvas so a mid-loop failure is safely
      // isolated to the last-attempted one-off.
      for (const { id, changes } of oneOffs) {
        const sessChanges = { ...changes };
        delete sessChanges.day_of_week;
        await api.updateSession(id, sessChanges);
      }
      setPendingChanges({});
      setNewSessions([]);
      setUnplacedBlocks([]);
      setArmedGroupIds(null);
      if (onSaved) await onSaved();
      toast.success(`${changeCount} change${changeCount !== 1 ? "s" : ""} saved`);
      // Iter 33d Follow-up 6 — Run validation against the freshly-saved
      // schedule and surface any violations inline. Failures here are
      // non-blocking — the save itself already succeeded.
      runValidation();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      // FP3-109 — Backend returns a structured 409 with per-block
      // conflicts. Unwrap → drive the inline banner + toast.
      if (detail && typeof detail === "object") {
        const conflicts = Array.isArray(detail.conflicts)
          ? detail.conflicts
          : [detail];
        setSaveConflicts(conflicts);
        const first = conflicts[0] || {};
        const msg =
          first.message ||
          detail.message ||
          "Could not save your changes. Fix the flagged blocks and try again.";
        // Do NOT clear pendingChanges/newSessions — the provider must
        // be able to correct the block and retry without losing work.
        toast.error(msg, {
          duration: 9000,
          description: conflicts.length > 1
            ? `${conflicts.length} blocks flagged. Review each one.`
            : undefined,
        });
      } else {
        console.error(e);
        const fallback = (typeof detail === "string" && detail)
          ? detail
          : (e?.message || "Unknown error");
        toast.error(`Failed to save: ${fallback}`);
      }
    } finally {
      setSaving(false);
    }
  };

  const runValidation = async () => {
    if (!provider?.id || !activePeriod?.id) return;
    setValidationLoading(true);
    try {
      const r = await api.getScheduleHealthCheck({
        provider_id: provider.id,
        schedule_period_id: activePeriod.id,
      });
      setValidationViolations(r.data?.violations || []);
      setValidationLastRunAt(Date.now());
    } catch (err) {
      console.error("validation failed", err);
      toast.error("Validation check failed — your save was successful");
    } finally {
      setValidationLoading(false);
    }
  };

  // Build the set of session ids that should render with the amber
  // validation ring. health-check returns block_id; mySessions are
  // materialized as `rb__<block_id>__<date>`, so we resolve via
  // _recurring_block_id. BuilderBlock also accepts a raw block_id match
  // so we add both forms here for completeness.
  //
  // FP3-111B — When the viewer has Service Fidelity Monitoring OFF
  // (viewer preference — not org-level), suppress the three
  // fidelity-only kinds from every downstream consumer (the pill
  // count, the drawer, the amber ring on blocks) so the workspace
  // shows ONLY hard scheduling errors. Hard conflicts (student /
  // provider double-book, constraint conflicts, day/time errors) are
  // never suppressed.
  const { isOff: isFidelityOff, isReady: fidelityReady } = useFidelityPref();
  const FIDELITY_ONLY_KINDS = useMemo(
    () => new Set([
      "service_obligation_no_blocks",
      "service_obligation_under_minutes",
      "service_obligation_over_minutes",
    ]),
    [],
  );
  const visibleValidationViolations = useMemo(() => {
    if (!fidelityReady) {
      // Default-hide during bootstrap to avoid a flash.
      return validationViolations.filter((v) => !FIDELITY_ONLY_KINDS.has(v.kind));
    }
    if (!isFidelityOff) return validationViolations;
    return validationViolations.filter((v) => !FIDELITY_ONLY_KINDS.has(v.kind));
  }, [validationViolations, isFidelityOff, fidelityReady, FIDELITY_ONLY_KINDS]);

  const validationSet = useMemo(() => {
    const s = new Set();
    visibleValidationViolations.forEach((v) => {
      if (v.block_id) s.add(v.block_id);
    });
    return s;
  }, [visibleValidationViolations]);

  // Jump to a flagged block on the canvas (smooth scroll). Used by the
  // "Show on canvas" button in the validation panel.
  const handleValidationJump = (blockId) => {
    const el = document.querySelector(`[data-block-ref="${blockId}"]`);
    if (el && el.scrollIntoView) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  };

  // Apply a suggested alternative slot.
  // FP3-067 — Apply now ALWAYS persists immediately and re-runs
  // validation. Previously same-day suggestions were staged into
  // pendingChanges so the user had to click Save manually — that
  // left the violation row visible even after Apply, which read as
  // "Apply didn't do anything". Now both same-day and cross-day
  // suggestions go through the same write-through path:
  //   1. PUT /recurring-blocks/{id} with the new day/time
  //   2. Refresh the schedule (so canvas updates immediately)
  //   3. Re-run validation so the resolved row disappears and any
  //      remaining issues are shown.
  const handleApplyAlternative = async (blockId, sess, alt) => {
    if (!alt) return;
    const dow =
      typeof alt.day_of_week === "number"
        ? alt.day_of_week
        : sess
        ? sess.day_of_week
        : undefined;
    if (typeof dow !== "number") {
      toast.error("Cannot apply this suggestion — missing day target.");
      return;
    }
    try {
      await api.updateRecurringBlock(blockId, {
        day_of_week: dow,
        start_time: alt.start_time,
        end_time: alt.end_time,
      });
      if (onSaved) await onSaved();
      toast.success(
        `Block moved to ${DAYS[dow]} ${alt.start_time}–${alt.end_time}`
      );
      runValidation();
      // Scroll the moved block into view for confirmation.
      handleValidationJump(blockId);
    } catch (e) {
      toast.error(`Failed to move block: ${e.message || ""}`);
    }
  };

  const handleRevert = () => {
    const reverted = newSessions
      .map((ns) => ({
        id: `up-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
        student: ns.students?.[0],
        duration: toMin(ns.end_time) - toMin(ns.start_time),
        service_type: ns.service_type,
        block_kind: ns.block_kind || "service",
        // FP3-065 follow-up #4 — Preserve the monthly cadence so a
        // reverted-then-redropped block keeps its commitment-aware
        // `weeks_of_month` mask.
        weeks_of_month: ns.weeks_of_month || null,
      }))
      .filter((b) => b.student);
    setUnplacedBlocks((prev) => [...prev, ...reverted]);
    setPendingChanges({});
    setNewSessions([]);
    setArmedGroupIds(null);
    setSaveConflicts([]);
    toast.info("Changes reverted");
  };

  // Render the drag preview ghost in a row's drop zone
  const renderDragPreview = (dayIdx) => {
    if (!dragPreview || dragPreview.dayIdx !== dayIdx) return null;
    const { startMin, dur, conflict } = dragPreview;
    const color = currentDragItem?.color || { bg: "#1E3F33", border: "#1E3F33" };
    // FP3-065 follow-up #7 audit — when the candidate slot
    // overlaps a student constraint, render the ghost in a red
    // warning state instead of the normal green, AND attach a
    // small tooltip describing the constraint so the operator
    // sees the cause before releasing the mouse.
    const warn = !!conflict;
    return (
      <div
        data-testid={`drag-preview-${dayIdx}`}
        className="absolute pointer-events-none rounded border-2 border-dashed flex items-center justify-center z-30 transition-all duration-75"
        style={{
          left: `${pct(startMin)}%`,
          width: `${(dur / TOTAL_MIN) * 100}%`,
          top: 4,
          height: 32,
          backgroundColor: warn ? "#FEE2E2DD" : color.bg + "AA",
          borderColor: warn ? "#B91C1C" : color.border,
        }}
      >
        <span
          className="text-[10px] font-semibold"
          style={{ color: warn ? "#991B1B" : color.text || "#1E3F33" }}
        >
          {warn
            ? (conflict.kind === "provider"
                ? `Provider rule · ${conflict.label}`
                : conflict.kind === "grade"
                ? conflict.label
                : `${conflict.studentName} · ${conflict.label}`)
            : `${fmtTime(toTime(startMin))} · ${dur}m`}
        </span>
      </div>
    );
  };

  return (
    <div
      className={`transition-[margin] duration-300 ease-out ${
        validationDrawerOpen ? "mr-[400px]" : "mr-0"
      }`}
      data-testid="builder-workspace-outer"
    >
    {/* FP3-124 — informational banner explaining effective-dated
        Builder behavior. Non-dismissible, soft neutral/green info
        treatment (never red / never warning). Placed above the
        two-column workspace so it reads as a page-level notice. */}
    <div
      className="mb-3 flex items-start gap-2.5 rounded-lg border border-[#D5E3D8] bg-[#F0F5F1] px-3 py-2 text-xs leading-relaxed text-[#2F4A3B]"
      data-testid="builder-effective-date-info"
      role="note"
    >
      <Info
        size={16}
        weight="duotone"
        className="mt-0.5 shrink-0 text-[#1E3F33]"
      />
      <p className="min-w-0">
        Changes made in Builder apply from today forward, so the current
        week may show both a past occurrence and the newly updated
        schedule.
      </p>
    </div>
    <div
      className="flex gap-4"
      data-testid="builder-workspace"
    >
      {/* ── Left Control Panel ─────────────────────────── */}
      <div className="w-60 shrink-0 space-y-3" data-testid="builder-panel">
        <div className="relative">
          <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#6B7280]" />
          <Input
            data-testid="builder-search"
            value={builderSearch}
            onChange={(e) => setBuilderSearch(e.target.value)}
            placeholder="Search students..."
            className="pl-8 h-8 text-xs bg-white border-[#d4d2cc] rounded-lg"
          />
        </div>

        <div className="border border-[#d4d2cc] rounded-lg bg-white overflow-hidden">
          <div className="px-2.5 py-1 bg-[#ECEAE4] border-b border-[#d4d2cc] flex items-center justify-between">
            <span className="text-[9px] font-semibold text-[#4B5563] uppercase tracking-wider">Students</span>
            <span className="text-[9px] text-[#9B9890] tabular-nums">{panelStudents.length}</span>
          </div>
          <ScrollArea className="h-52">
            {panelStudents.map((s) => {
              const color = gradeColor(s.grade);
              const isSelected = selectedStudent?.id === s.id;
              // Iter 32 — dedupe on _recurring_block_id so weekly blocks
              // aren't counted per-weekday occurrence.
              const placedBlocks = new Set();
              mySessions.forEach((sess) => {
                if (sess.student_ids?.includes(s.id)) {
                  placedBlocks.add(sess._recurring_block_id || sess.id);
                }
              });
              const placedCount = placedBlocks.size;
              const trayCount = unplacedBlocks.filter((b) => b.student?.id === s.id).length;
              const total = placedCount + trayCount;
              // FP3-106D — Monthly-service-minutes badge. Uses the
              // canonical `studentMonthlyMinutes` normaliser so the
              // label lines up with the Wizard's expected/placed math
              // (see routes/wizard.py::expected_minutes_per_month).
              const monthlyMinutes = studentMonthlyMinutes(s);
              const monthlyLabel = formatMonthlyMinutes(monthlyMinutes);
              return (
                <button
                  key={s.id}
                  data-testid={`builder-student-${s.id}`}
                  onClick={() => setSelectedStudent(isSelected ? null : s)}
                  className={`w-full flex items-center gap-1.5 px-2 py-1 text-left text-[11px] transition-colors ${
                    isSelected ? "bg-[#E8F0EB] text-[#1E3F33]" : "hover:bg-[#FCFBF9] text-[#1F2937]"
                  }`}
                >
                  <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ backgroundColor: color.border }} />
                  <span className="truncate">
                    {s.first_name} {s.last_name?.[0]}.
                  </span>
                  <span className="text-[9px] text-[#9B9890] ml-auto shrink-0 tabular-nums">{s.grade}</span>
                  <span
                    className="text-[9px] text-[#6B7280] shrink-0 tabular-nums"
                    data-testid={`builder-student-${s.id}-monthly-minutes`}
                    title={
                      monthlyMinutes == null
                        ? "No direct-service commitment recorded"
                        : `Monthly service commitment: ${monthlyLabel}`
                    }
                  >
                    {monthlyLabel}
                  </span>
                  {total > 0 && (
                    <span
                      className="text-[9px] font-semibold tabular-nums px-1 rounded shrink-0"
                      style={{ backgroundColor: color.bg, color: color.text }}
                      title={`${placedCount} placed · ${trayCount} unplaced`}
                    >
                      {total}
                    </span>
                  )}
                </button>
              );
            })}
          </ScrollArea>
        </div>

        {/* Iter 33d Follow-up 9 — Single block-creator card with two
            accordion tabs (Service / Personal). Only one is visible at a
            time so the left rail stays compact. Tab auto-switches to
            Service when a student is selected. */}
        <div className="border border-[#d4d2cc] rounded-lg bg-white overflow-hidden" data-testid="builder-block-creator">
          <div className="flex border-b border-[#d4d2cc] bg-[#ECEAE4]" role="tablist">
            <button
              type="button"
              role="tab"
              aria-selected={creatorTab === "service"}
              data-testid="creator-tab-service"
              onClick={() => setCreatorTab("service")}
              className={`flex-1 px-2 py-1.5 text-[10px] font-semibold uppercase tracking-wider transition-colors ${
                creatorTab === "service"
                  ? "bg-white text-[#1E3F33]"
                  : "text-[#6B7280] hover:text-[#1F2937] hover:bg-[#E0DDD3]"
              }`}
            >
              Service block
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={creatorTab === "personal"}
              data-testid="creator-tab-personal"
              onClick={() => setCreatorTab("personal")}
              className={`flex-1 px-2 py-1.5 text-[10px] font-semibold uppercase tracking-wider border-l border-[#d4d2cc] transition-colors ${
                creatorTab === "personal"
                  ? "bg-white text-[#1E3F33]"
                  : "text-[#6B7280] hover:text-[#1F2937] hover:bg-[#E0DDD3]"
              }`}
            >
              Personal block
            </button>
          </div>

          {creatorTab === "service" && (
            <div data-testid="creator-pane-service">
              {selectedStudent ? (
                <div>
                  <div className="px-2.5 py-1.5 border-b border-[#ECEAE4] flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-[11px] font-semibold text-[#1F2937] truncate">
                        {selectedStudent.first_name} {selectedStudent.last_name}
                      </p>
                      <p className="text-[9px] text-[#9B9890] mt-0.5 truncate">
                        Gr {selectedStudent.grade} ·{" "}
                        {/* FP3-035B-2 correction — show only the
                            service labels this provider is
                            responsible for on this student. Falls
                            back to "—" when the provider has no
                            assigned domain intersection. */}
                        {viewerServicesForStudent(selectedStudent).join(", ") || "—"}
                      </p>
                    </div>
                    <span
                      data-testid="student-block-count"
                      className="text-[9px] font-semibold tabular-nums px-1.5 py-0.5 rounded bg-[#E8F0EB] text-[#1E3F33] shrink-0"
                      title="Total blocks for this student (placed + unplaced)"
                    >
                      {studentBlockCount}
                    </span>
                  </div>
                  <div className="p-2">
                    {/* FP3-034H — 9 options (5–45 in 5-min steps) so the
                        Add Block palette covers common 20 / 25 / 35 / 40 /
                        45-minute push-in slots without forcing providers
                        to drag-resize after creation. grid-cols-5 wraps
                        cleanly into 5 + 4 rows; the wider grid leaves
                        the chip readable at 5m..45m. */}
                    <div className="grid grid-cols-5 gap-1">
                      {SERVICE_BLOCK_MINUTE_OPTIONS.map((dur) => (
                        <button
                          key={dur}
                          data-testid={`add-block-${dur}`}
                          onClick={() => addBlock(dur)}
                          className="py-1.5 rounded text-[11px] font-semibold transition-all bg-[#F3F1EC] text-[#4B5563] hover:bg-[#E8F0EB] hover:text-[#1E3F33] active:scale-95"
                        >
                          {dur}m
                        </button>
                      ))}
                    </div>
                    <p className="text-[9px] text-[#9B9890] mt-1.5">
                      Click to add · drag onto canvas
                    </p>
                  </div>
                </div>
              ) : (
                <div className="px-3 py-3 text-[11px] text-[#6B7280] text-center">
                  Pick a student above to add a service block.
                </div>
              )}
            </div>
          )}

          {creatorTab === "personal" && (
            <div data-testid="creator-pane-personal" className="p-2 space-y-1">
              {[
                { kind: "lunch", label: "Lunch", dur: 30 },
                { kind: "prep", label: "Prep time", dur: 30 },
                { kind: "duties", label: "Duties", dur: 30 },
                { kind: "testing", label: "Testing", dur: 60 },
                { kind: "meeting", label: "Meeting", dur: 30 },
                { kind: "other", label: "Other", dur: 30 },
              ].map(({ kind, label, dur }) => (
                <button
                  key={kind}
                  data-testid={`add-personal-${kind}`}
                  onClick={() => addPersonalBlock(kind, dur, label)}
                  className="w-full flex items-center justify-between px-2 py-1 rounded-md text-[11px] font-medium bg-[#F3F1EC] text-[#4B5563] hover:bg-[#E8F0EB] hover:text-[#1E3F33] active:scale-95 transition-all"
                >
                  <span>{label}</span>
                  <span className="text-[9px] text-[#9B9890] tabular-nums">{dur}m</span>
                </button>
              ))}
            </div>
          )}
        </div>

        {unplacedBlocks.length > 0 && (
          <div className="border border-[#d4d2cc] rounded-lg bg-white overflow-hidden" data-testid="unplaced-blocks">
            <div className="px-2.5 py-1.5 bg-[#ECEAE4] border-b border-[#d4d2cc] flex items-center gap-1.5">
              <Stack size={11} className="text-[#4B5563]" />
              <span className="text-[10px] font-semibold text-[#4B5563] uppercase tracking-wider">
                Unplaced ({unplacedBlocks.length})
              </span>
            </div>
            <ScrollArea className="max-h-48">
              <div className="p-1.5 space-y-1">
                {unplacedBlocks.map((block) => {
                  const isPersonal = block.block_kind === "personal";
                  const color = isPersonal
                    ? { bg: "#F3F1EC", border: "#9B9890", text: "#4B5563" }
                    : (gradeColor(block.student?.grade));
                  return (
                    <div
                      key={block.id}
                      draggable
                      data-testid={`unplaced-${block.id}`}
                      onDragStart={(e) => {
                        const payload = {
                          type: "new",
                          blockId: block.id,
                          studentId: block.student?.id,
                          student: block.student,
                          dur: block.duration,
                          service_type: block.service_type,
                          block_kind: block.block_kind || "service",
                          personal_kind: block.personal_kind || null,
                          personal_label: block.personal_label || "",
                          // FP3-065 follow-up #4 — Carry the
                          // student's canonical monthly cadence on
                          // the drag payload so the dropped block
                          // saves with the same `weeks_of_month`
                          // mask the wizard would have used.
                          weeks_of_month: block.weeks_of_month || null,
                        };
                        e.dataTransfer.setData("application/json", JSON.stringify(payload));
                        e.dataTransfer.effectAllowed = "all";
                        e.currentTarget.style.opacity = "0.4";
                        setCurrentDragItem({ ...payload, color });
                      }}
                      onDragEnd={(e) => {
                        e.currentTarget.style.opacity = "1";
                        setCurrentDragItem(null);
                        setDragPreview(null);
                      }}
                      className="flex items-center gap-2 px-2 py-1.5 rounded cursor-grab active:cursor-grabbing border hover:shadow-sm transition-shadow"
                      style={{ backgroundColor: color.bg, borderColor: color.border, borderLeft: `3px solid ${color.border}` }}
                    >
                      <div className="flex-1 min-w-0">
                        <p className="text-[10px] font-semibold truncate" style={{ color: color.text }}>
                          {isPersonal
                            ? (block.personal_label || block.personal_kind || "Personal")
                            : `${block.student.first_name} ${block.student.last_name?.[0]}.`}
                        </p>
                        <p className="text-[9px]" style={{ color: color.text + "90" }}>
                          {block.duration}m &middot;{" "}
                          {isPersonal ? `${block.personal_kind || "personal"}` : block.service_type}
                        </p>
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          removeUnplaced(block.id);
                        }}
                        className="text-[#6B7280] hover:text-[#B85C5C] transition-colors p-0.5"
                        data-testid={`remove-unplaced-${block.id}`}
                      >
                        <Trash size={11} />
                      </button>
                    </div>
                  );
                })}
              </div>
            </ScrollArea>
          </div>
        )}
      </div>

      {/* ── Right: Agenda Canvas ───────────────────────── */}
      <div className="flex-1 min-w-0">
        <div
          className="flex items-center gap-2 mb-2"
          data-testid="builder-action-bar"
        >
          {hasChanges && (
            <div
              className="flex items-center gap-2 animate-fade-in"
              data-testid="builder-save-bar"
            >
              <Badge className="bg-[#FDF8F3] text-[#D9A05B] hover:bg-[#FDF8F3] text-xs">
                {changeCount} unsaved
              </Badge>
              <Button
                onClick={handleRevert}
                variant="outline"
                size="sm"
                className="border-[#E5E3DB] text-[#6B7280] rounded-lg h-7 text-xs"
                data-testid="builder-revert"
              >
                <ArrowCounterClockwise size={12} className="mr-1" />
                Revert
              </Button>
              <Button
                onClick={handleSave}
                disabled={saving}
                size="sm"
                className="bg-[#1E3F33] hover:bg-[#173127] text-white rounded-lg h-7 text-xs"
                data-testid="builder-save"
              >
                <FloppyDisk size={12} className="mr-1" />
                {saving ? "Saving..." : "Save"}
              </Button>
            </div>
          )}
          {/* FP3-113 — Export the current Builder grid state as CSV.
              Frontend-only (uses displaySessions in memory); does not
              modify data. Placed in the left cluster alongside Save /
              Revert / Clear All so it stays near the Schedule heading
              and never floats at the far-right edge. */}
          <Button
            onClick={handleExportGridCsv}
            variant="outline"
            size="sm"
            className="border-[#E5E3DB] text-[#3F4756] hover:bg-[#F3F1EC] rounded-lg h-7 text-xs"
            data-testid="builder-export-grid-csv-btn"
          >
            <DownloadSimple size={12} className="mr-1" />
            Export Grid CSV
          </Button>
          {/* FP3-106C — Prominent, destructive Clear All action. Always
              visible (does not hide inside a menu) so a provider can
              reset the entire schedule + Wizard state for the selected
              period and start fresh. */}
          {onOpenClearAll && activePeriod?.id && (
            <Button
              onClick={onOpenClearAll}
              variant="outline"
              size="sm"
              className="ml-auto border-[#E7CFCB] text-[#7A2F2F] hover:bg-[#FBF3F1] hover:text-[#5F2323] rounded-lg h-7 text-xs"
              data-testid="builder-clear-all-btn"
            >
              <Trash size={12} className="mr-1" />
              Clear All
            </Button>
          )}
        </div>

        {/* FP3-115 — Grade constraint overlay control. Compact chip
            row that lives ABOVE the canvas next to the action bar so
            it's discoverable without dominating the layout. Overlay
            state is local to this Builder visit only (never
            persisted, never in Save payloads, never marks a change).
            Existing service blocks stay visually dominant because the
            overlay renders at zIndex 0 with pointer-events: none. */}
        <div
          className="mb-2 flex items-center gap-1.5 flex-wrap"
          data-testid="grade-overlay-control"
        >
          <span className="text-[10px] uppercase tracking-wide text-[#6B7280] font-medium mr-1">
            Grade overlay
          </span>
          <button
            type="button"
            data-testid="grade-overlay-off"
            onClick={() => {
              setManualOverlayGrade("");
              setManualOverlayPattern("");
            }}
            className={`px-2.5 py-1 rounded-md text-[11px] font-medium border transition ${
              !manualOverlayGrade
                ? "bg-[#1E3F33] text-white border-[#1E3F33]"
                : "bg-white text-[#6B7280] border-[#E5E3DB] hover:border-[#9B9890]"
            }`}
          >
            Off
          </button>
          {availableGradesForOverlay.map((g) => (
            <button
              key={g}
              type="button"
              data-testid={`grade-overlay-grade-${g}`}
              onClick={() => {
                setManualOverlayGrade(g);
                setManualOverlayPattern("");
              }}
              className={`px-2.5 py-1 rounded-md text-[11px] font-medium border transition ${
                manualOverlayGrade === g
                  ? "bg-[#9C6B2E] text-white border-[#9C6B2E]"
                  : "bg-white text-[#6B7280] border-[#E5E3DB] hover:border-[#9B9890]"
              }`}
            >
              {g === "K" ? "K" : g}
            </button>
          ))}
          {overlaySchools.length > 1 && manualOverlayGrade && (
            <select
              data-testid="grade-overlay-school-select"
              value={overlayScopedSchoolId}
              onChange={(e) => {
                setManualOverlaySchoolId(e.target.value);
                setManualOverlayGrade("");
                setManualOverlayPattern("");
              }}
              className="text-[11px] rounded-md border border-[#E5E3DB] bg-white text-[#3F4756] px-1.5 py-1 h-7"
            >
              {overlaySchools.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name || s.id.slice(0, 8)}
                </option>
              ))}
            </select>
          )}
          {manualOverlayGrade && availablePatternsForOverlay.length > 1 && (
            <select
              data-testid="grade-overlay-pattern-select"
              value={
                manualOverlayPattern ||
                (availablePatternsForOverlay.includes("full_day")
                  ? "full_day"
                  : availablePatternsForOverlay[0])
              }
              onChange={(e) => setManualOverlayPattern(e.target.value)}
              className="text-[11px] rounded-md border border-[#E5E3DB] bg-white text-[#3F4756] px-1.5 py-1 h-7"
            >
              {availablePatternsForOverlay.map((p) => (
                <option key={p} value={p}>
                  {p === "am" ? "AM" : p === "pm" ? "PM" : "Full Day"}
                </option>
              ))}
            </select>
          )}
          {overlayContext && overlayContext.source !== "student" && (
            <span
              data-testid="grade-overlay-legend"
              className="text-[10px] text-[#9C6B2E] ml-1 italic"
            >
              {overlayContext.grade === "K"
                ? "Kindergarten"
                : `Grade ${overlayContext.grade}`}
              {" · "}
              {overlayContext.pattern === "am"
                ? "AM"
                : overlayContext.pattern === "pm"
                ? "PM"
                : "Full Day"}
              {" constraints shown"}
            </span>
          )}
          {overlayContext?.source === "student" && (() => {
            // FP3-115B — Selected-student legend (spec §5 / §15).
            // Explains WHY overlays are shown while the manual
            // Grade Overlay pill still says Off.
            const hasGrade = overlayBlocks.length > 0;
            const hasStudent = studentOverlayBlocks.length > 0;
            const patternLabel =
              overlayContext.pattern === "am"
                ? "AM"
                : overlayContext.pattern === "pm"
                ? "PM"
                : "Full Day";
            const gradeLabel =
              overlayContext.grade === "K"
                ? "Kindergarten"
                : `Grade ${overlayContext.grade}`;
            let body;
            if (hasGrade && hasStudent) {
              body = (
                <>
                  {`${overlayContext.studentName} · ${gradeLabel} · ${patternLabel}`}
                  <span className="ml-1">
                    Grade constraints + student constraints shown
                  </span>
                </>
              );
            } else if (hasGrade && !hasStudent) {
              body = (
                <>
                  {`${overlayContext.studentName} · ${gradeLabel} · ${patternLabel}`}
                  <span className="ml-1">
                    Grade constraints shown · No student-specific constraints
                  </span>
                </>
              );
            } else if (!hasGrade && hasStudent) {
              body = (
                <>
                  {overlayContext.studentName}
                  <span className="ml-1">Student constraints shown</span>
                </>
              );
            } else {
              // Spec §15 — empty case: no grade rules AND no
              // student constraints. Do not shade the canvas.
              return (
                <span
                  data-testid="student-overlay-empty"
                  className="text-[10px] text-[#6B7280] ml-1 italic"
                >
                  {`No active scheduling constraints for ${overlayContext.studentName} this week.`}
                </span>
              );
            }
            return (
              <span
                data-testid="student-overlay-legend"
                className="text-[10px] text-[#3D4B7A] ml-1 italic"
              >
                {body}
              </span>
            );
          })()}
          {overlayContext?.source === "manual" &&
            manualOverlayGrade &&
            overlayBlocks.length === 0 && (
              <span
                data-testid="grade-overlay-empty"
                className="text-[10px] text-[#6B7280] ml-1 italic"
              >
                No active
                {manualOverlayGrade === "K"
                  ? " Kindergarten"
                  : ` Grade ${manualOverlayGrade}`}
                {" constraints for this week."}
              </span>
            )}
        </div>

        {/* FP3-109 — Structured Save-conflict banner. Rendered inline
            above the canvas so the provider immediately sees which
            student / day / time / rule prevented the atomic Save. The
            unsaved-change tray is preserved by handleSave, so the
            provider can edit or remove the flagged block and retry. */}
        {saveConflicts.length > 0 && (
          <div
            className="mb-2 rounded-lg border border-[#E7CFCB] bg-[#FBF3F1] px-3 py-2"
            data-testid="builder-save-conflict-banner"
          >
            <div className="flex items-start justify-between gap-2">
              <div className="text-[11px] font-semibold uppercase tracking-wider text-[#7A2F2F]">
                Couldn&apos;t save {saveConflicts.length} block
                {saveConflicts.length !== 1 ? "s" : ""}
              </div>
              <button
                type="button"
                onClick={() => setSaveConflicts([])}
                data-testid="builder-save-conflict-dismiss"
                className="text-[10px] text-[#7A2F2F] hover:underline"
              >
                Dismiss
              </button>
            </div>
            <ul className="mt-1.5 space-y-1 text-xs text-[#4B2222]">
              {saveConflicts.map((c, idx) => (
                <li
                  key={c.block_id || c.client_id || c.constraint_id || idx}
                  data-testid={`builder-save-conflict-${idx}`}
                  className="leading-snug"
                >
                  {c.message ||
                    `Conflict on ${c.day_label || "this day"} ${c.start_time || ""}–${c.end_time || ""}`}
                </li>
              ))}
            </ul>
            <p className="mt-1.5 text-[10px] text-[#6B7280]">
              Your unsaved changes are preserved. Edit or remove the flagged
              blocks and try Save again.
            </p>
          </div>
        )}


        {/* Iter 33d Follow-up 9 — Compact validation pill above the
            canvas. Replaces the always-mounted full panel below the
            canvas. Click to open/close the right-side drawer.

            FP3-075B — Suppressed in Simple Mode. Simple Mode users
            aren't running constraint/rules/health validation as a
            primary workflow; the global "Schedule is clean / N
            issues" banner is exactly the advanced-language surface
            we want to keep behind Advanced Mode. */}
        {validationLastRunAt && !validationLoading && !isSimpleMode && (
          <button
            type="button"
            data-testid="builder-validation-pill"
            onClick={() => setValidationDrawerOpen((v) => !v)}
            className={`w-full flex items-center gap-2 mb-2 px-3 py-1.5 rounded-lg border text-left transition-colors ${
              visibleValidationViolations.length > 0
                ? "border-[#F2DBC0] bg-[#FDF8F3] hover:bg-[#FBEFE0]"
                : "border-[#D6E4DA] bg-[#F1F7F3] hover:bg-[#E7F1EB]"
            }`}
          >
            {visibleValidationViolations.length > 0 ? (
              <>
                <span className="w-1.5 h-1.5 rounded-full bg-[#D9A05B] shrink-0" aria-hidden />
                <span className="text-[11px] font-semibold text-[#9C6B2E]">
                  {visibleValidationViolations.length} validation issue{visibleValidationViolations.length !== 1 ? "s" : ""}
                </span>
                <span className="text-[10px] text-[#9C6B2E]/80 ml-auto">
                  {validationDrawerOpen ? "Hide details" : "Review →"}
                </span>
              </>
            ) : (
              <>
                <span className="w-1.5 h-1.5 rounded-full bg-[#1E3F33] shrink-0" aria-hidden />
                <span className="text-[11px] font-medium text-[#1E3F33]">
                  Schedule is clean — no validation issues
                </span>
                <span className="text-[10px] text-[#1E3F33]/70 ml-auto">
                  {validationDrawerOpen ? "Hide" : "View"}
                </span>
              </>
            )}
          </button>
        )}

        <div
          ref={gridScrollRef}
          className="border border-[#d4d2cc] rounded-xl overflow-x-auto bg-[#F9F8F5] builder-grid-scroll"
          data-testid="builder-grid"
          onDragLeave={handleDragLeave}
        >
          <div style={{ minWidth: "900px" }}>
            <TimeHeader leftWidth={80} rightWidth={80} />
            {weekDates.map((date, dayIdx) => {
              const { sorted, count } = builderDayMap[date] || { sorted: [], count: 1 };
              const rowHeight = Math.max(ROW_MIN_HEIGHT, count * LANE_PX + 16);
              return (
                <div
                  key={date}
                  className="flex border-b border-[#d4d2cc] last:border-b-0"
                  data-testid={`builder-row-${dayIdx}`}
                >
                  {/* Left day label */}
                  <div
                    className="w-20 shrink-0 border-r border-[#d4d2cc] flex items-center justify-center bg-[#ECEAE4]"
                    style={{ minHeight: rowHeight }}
                  >
                    <span className="text-[11px] font-bold text-[#4B5563] heading-font [writing-mode:vertical-rl] rotate-180 tracking-wider">
                      {DAYS[dayIdx]}
                    </span>
                  </div>

                  {/* Drop zone */}
                  <div
                    className="flex-1 relative bg-white/80 transition-colors overflow-hidden"
                    style={{ minHeight: rowHeight }}
                    onDragOver={(e) => handleDragOver(e, dayIdx)}
                    onDrop={(e) => handleDrop(e, dayIdx)}
                    data-testid={`builder-drop-${dayIdx}`}
                  >
                    {/* Hour gridlines */}
                    {HOURS.map((h) => {
                      const l = pct(h * 60);
                      return l >= 0 && l <= 100 ? (
                        <div
                          key={h}
                          className="absolute top-0 bottom-0 border-l border-[#d4d2cc]/60"
                          style={{ left: `${l}%` }}
                        />
                      ) : null;
                    })}
                    {/* Half-hour gridlines (faint) */}
                    {HOURS.map((h) => {
                      const l = pct(h * 60 + 30);
                      return l >= 0 && l <= 100 ? (
                        <div
                          key={`h${h}`}
                          className="absolute top-0 bottom-0 border-l border-dashed border-[#d4d2cc]/35"
                          style={{ left: `${l}%` }}
                        />
                      ) : null;
                    })}
                    {/* 15-min minor gridlines (very faint) */}
                    {HOURS.flatMap((h) =>
                      [15, 45].map((m) => {
                        const l = pct(h * 60 + m);
                        return l >= 0 && l <= 100 ? (
                          <div
                            key={`q${h}-${m}`}
                            className="absolute top-0 bottom-0 border-l border-[#d4d2cc]/20"
                            style={{ left: `${l}%` }}
                          />
                        ) : null;
                      })
                    )}

                    {/* FP3-115 — Grade constraint overlay. Rendered
                        FIRST inside the drop zone so shared and
                        placed blocks paint on top. `pointer-events:
                        none` so drag/drop is never intercepted; the
                        overlay is purely informational — the existing
                        `findGradeConstraintConflict` still gates
                        actual placement and the backend validator is
                        still authoritative. Never included in Save
                        payloads or unsaved-change state. */}
                    <GradeConstraintOverlay
                      blocks={overlayBlocks}
                      dayIdx={dayIdx}
                      gradeLabel={overlayContext?.grade || ""}
                    />

                    {/* FP3-115B — Student-specific constraint
                        overlay. Rendered only when a student is
                        selected in the Students box (§6.B/§6.D).
                        Visually distinct from the grade overlay
                        (cool blue-slate hatched vs warm amber
                        hatched — §4) so overlapping rules remain
                        individually readable. Same
                        `pointer-events: none` + `zIndex: 0`
                        contract; actual drop-blocking still lives
                        in `findStudentConstraintConflict`. */}
                    <StudentConstraintOverlay
                      blocks={studentOverlayBlocks}
                      dayIdx={dayIdx}
                    />

                    {/* Iter 31 — School-context overlay (read-only ghost
                        blocks). Other providers' sessions involving my
                        students for THIS weekday. Rendered BEFORE the
                        provider's own blocks so own blocks paint on top
                        and remain visually primary. */}
                    {(sharedSchoolSessions || [])
                      .filter((ss) => {
                        // Match weekday by index in weekDates.
                        const idx = (weekDates || []).indexOf(ss.date);
                        return idx === dayIdx;
                      })
                      .map((ss) => {
                        const startMin = (() => {
                          const [h, m] = (ss.start_time || "0:0").split(":").map(Number);
                          return h * 60 + m;
                        })();
                        const endMin = (() => {
                          const [h, m] = (ss.end_time || "0:0").split(":").map(Number);
                          return h * 60 + m;
                        })();
                        const left = pct(startMin);
                        const width = pct(endMin) - pct(startMin);
                        const isShared = sharedConflictSet?.has(ss.id);
                        return (
                          <div
                            key={`shared-${ss.id}`}
                            data-testid={`builder-shared-${ss.id}`}
                            data-shared="true"
                            className={`absolute select-none rounded overflow-hidden pointer-events-auto cursor-not-allowed border border-dashed ${
                              isShared
                                ? "border-[#D9A05B] ring-1 ring-[#D9A05B]/40"
                                : "border-[#cfcfc6]"
                            }`}
                            style={{
                              left: `${left}%`,
                              width: `${Math.max(0.5, width)}%`,
                              top: "4%",
                              bottom: "4%",
                              backgroundColor: "rgba(243, 241, 236, 0.55)",
                              backgroundImage:
                                "repeating-linear-gradient(135deg, transparent 0 6px, rgba(0,0,0,0.05) 6px 7px)",
                              zIndex: 0,
                            }}
                            title={`${ss.owner_name || "Other provider"} · ${
                              ss.service_type || ""
                            } — read-only school context`}
                          >
                            <div className="px-1.5 py-0.5 text-[9px] text-[#6B7280] truncate italic">
                              {ss.owner_name?.split(" ").slice(-1)[0] || "Other"}
                              {ss.service_type ? ` · ${ss.service_type}` : ""}
                            </div>
                          </div>
                        );
                      })}

                    {/* Placed blocks */}
                    {sorted.map((s) => {
                      const groupIds = groupMap.get(s.id) || [s.id];
                      const isArmed =
                        armedGroupIds && armedGroupIds.has(s.id) && groupIds.length > 1;
                      return (
                        <BuilderBlock
                          key={s.id}
                          session={s}
                          laneCount={count}
                          conflictSet={conflictSet}
                          validationSet={validationSet}
                          onResize={handleResize}
                          onDelete={handleDelete}
                          onDragItemStart={(item) => setCurrentDragItem(item)}
                          provider={provider}
                          groupIds={groupIds}
                          isArmedGroup={isArmed}
                          onArmGroup={(ids) => setArmedGroupIds(new Set(ids))}
                          shiftHeldRef={shiftHeldRef}
                          shiftHeld={shiftHeld}
                          applyGroupMove={applyGroupMove}
                          setDragPreview={setDragPreview}
                          highlightBlockId={highlightBlockId}
                        />
                      );
                    })}

                    {/* Drag preview ghost */}
                    {renderDragPreview(dayIdx)}
                  </div>

                  {/* Right day label (mirror) */}
                  <div
                    className="w-20 shrink-0 border-l border-[#d4d2cc] flex items-center justify-center bg-[#ECEAE4]"
                    style={{ minHeight: rowHeight }}
                  >
                    <span className="text-[11px] font-bold text-[#4B5563] heading-font [writing-mode:vertical-rl] rotate-180 tracking-wider">
                      {DAYS[dayIdx]}
                    </span>
                  </div>
                </div>
              );
            })}
            {/* Bottom time axis (mirror header) */}
            <TimeHeader leftWidth={80} rightWidth={80} />
          </div>
        </div>

        <div className="mt-3 flex items-center justify-between flex-wrap gap-2">
          <GradeLegend />
          <p className="text-[10px] text-[#6B7280]">
            Drag from Unplaced to canvas &middot; Drag right edge to resize &middot; Right-click block for actions &middot; Shift+drag to move group &middot; Snaps to 5 min
          </p>
        </div>
      </div>
      </div>

      {/* FP3-115A — Wide, mouse-friendly horizontal scrollbar sticky
          at the bottom of the Builder workspace. Only rendered when
          the grid actually overflows horizontally (spec §4). The
          inner grid's native scrollbar is hidden via the
          `.builder-grid-scroll` class so this is the ONE visible
          horizontal scrollbar. Track spans the full workspace width
          (bounded by PageShell max-w-[1520px] and starting after the
          fixed app sidebar per AppLayout's `<main class="flex-1 ...">`,
          so it never overlaps the app sidebar — spec §1). Trackpad
          + wheel scrolling on the grid still works; both surfaces
          sync bidirectionally through the effect above. Sticky at
          bottom-0 so it remains visible while the user scrolls
          vertically through the Builder page. */}
      {overflowState.show && (
        <div
          ref={phantomBarRef}
          data-testid="builder-bottom-scrollbar"
          className="builder-bottom-scrollbar rounded-lg mt-3 shadow-sm"
          aria-hidden="true"
          style={{
            position: "sticky",
            bottom: 0,
            overflowX: "scroll",
            overflowY: "hidden",
            height: 18,
            backgroundColor: "#ECEAE4",
            border: "1px solid #d4d2cc",
            zIndex: 20,
          }}
        >
          <div
            style={{
              width: overflowState.spacerWidth,
              height: 1,
            }}
          />
        </div>
      )}

      {/* Iter 33d Follow-up 9 — Validation drawer (right-side, non-modal).
          Replaces the always-mounted below-canvas panel so the canvas
          stays the primary visual focus. Auto-opens after a save with
          violations; user toggles via the summary pill above the canvas. */}
      <BuilderValidationDrawer
        open={validationDrawerOpen}
        onOpenChange={setValidationDrawerOpen}
        loading={validationLoading}
        violations={visibleValidationViolations}
        lastRunAt={validationLastRunAt}
        mySessions={mySessions}
        weekDates={weekDates}
        providerId={provider?.id}
        schedulePeriodId={activePeriod?.id}
        onJumpToBlock={handleValidationJump}
        onMoveBlock={handleApplyAlternative}
        onRecheck={runValidation}
        onRemoveBlock={async (blockId) => {
          // Iter 33d Follow-up 11 — Optimistically strip violations
          // tied to this block from the drawer BEFORE the silent
          // refresh completes. Otherwise the row lingers as a "ghost"
          // until the next health-check returns.
          setValidationViolations((prev) =>
            prev.filter((v) => v.block_id !== blockId)
          );
          // Iter 41 — Separate API error from refetch error.
          let removed = false;
          try {
            // FP3-124 §8 — Effective-dated delete from Validation drawer.
            const _n = new Date();
            const _eff = `${_n.getFullYear()}-${String(_n.getMonth() + 1).padStart(2, "0")}-${String(_n.getDate()).padStart(2, "0")}`;
            await api.deleteRecurringBlock(blockId, _eff);
            removed = true;
          } catch (err) {
            if (err?.response?.status === 404) {
              removed = true;
            } else {
              toast.error("Failed to remove block");
              runValidation();
              return;
            }
          }
          if (removed) {
            toast.success("Block removed");
            try {
              if (onSaved) await onSaved();
            } catch { /* refetch errors are non-fatal */ }
            runValidation();
          }
        }}
      />

      {/* Iter 33d Follow-up 10 — In-app delete-confirmation dialog
          replacing the native window.confirm popup. */}
      <AlertDialog
        open={!!pendingDeleteBlock}
        onOpenChange={(o) => {
          if (!o) setPendingDeleteBlock(null);
        }}
      >
        <AlertDialogContent
          data-testid="builder-delete-block-dialog"
          className="rounded-2xl border-[#E5E3DB]"
        >
          {(() => {
            // FP3-121A — Occurrence-level default.
            //
            // Compute once so the header, description, and action
            // buttons all reflect the same context. The provider's
            // DEFAULT action for a multi-day parent block is to
            // remove ONLY the clicked day (leaving the other days
            // untouched). Series-level removal is available as a
            // secondary "Remove entire series" action.
            const b = pendingDeleteBlock || {};
            const isSessionRow = b._deleteKind === "session_row";
            const daysMask = Array.isArray(b._days_of_week) ? b._days_of_week : null;
            const isMultiDay = !!daysMask && daysMask.length > 1;
            const dowLabels = ["Mon", "Tue", "Wed", "Thu", "Fri"];
            const parentDays = daysMask
              ? daysMask.map((n) => dowLabels[n]).filter(Boolean)
              : (typeof b._day_of_week === "number"
                  ? [dowLabels[b._day_of_week]].filter(Boolean)
                  : []);
            // The specific day the provider right-clicked. For a
            // materialized session (rb__), `date` is the exact date
            // being edited; fall back to the block's own day if
            // date is missing.
            let clickedDay = null;
            if (b.date) {
              const jsDow = new Date(b.date + "T12:00:00").getDay();
              const idx = jsDow === 0 ? 6 : jsDow - 1;
              if (idx >= 0 && idx <= 4) clickedDay = { idx, label: dowLabels[idx] };
            } else if (typeof b._day_of_week === "number") {
              clickedDay = { idx: b._day_of_week, label: dowLabels[b._day_of_week] };
            }
            const students = (b.students || [])
              .map((s) => `${s.first_name} ${s.last_name || ""}`.trim())
              .filter(Boolean);
            const studentPart =
              students.length > 1
                ? `${students.length} students (${students.join(", ")})`
                : students[0] || "this block";
            const remainingDays = clickedDay && parentDays.length > 0
              ? parentDays.filter((_, i) => (daysMask ? daysMask[i] !== clickedDay.idx : true))
              : [];
            const remainingLabel = remainingDays.join(", ");

            let title = "Remove this recurring block?";
            let description = "";
            if (isSessionRow) {
              title = "Remove this session?";
              description = "This removes only this single session row. Any parent recurring block stays intact and will keep materializing future weeks.";
            } else if (isMultiDay && clickedDay) {
              title = `Remove ${clickedDay.label} only?`;
              description = `This removes ${studentPart} on ${clickedDay.label} for the rest of the active schedule period. ${remainingLabel} ${remainingDays.length > 1 ? "remain" : "remains"} unchanged. Nothing is written until you Save.`;
            } else {
              // Single-day parent — same UX as before (delete the
              // whole block outright).
              const dayPart =
                parentDays.length > 1
                  ? `every week on ${parentDays.join(", ")} (${parentDays.length} days per week)`
                  : parentDays.length === 1
                  ? `every week on ${parentDays[0]}`
                  : "every week";
              description = `This removes ${studentPart} ${dayPart} for the rest of the active schedule period. Past persisted sessions from this block will also be cleared.`;
            }

            return (
              <>
                <AlertDialogHeader>
                  <AlertDialogTitle
                    data-testid="builder-delete-block-title"
                    className="text-[#1F2937] heading-font"
                  >
                    {title}
                  </AlertDialogTitle>
                  <AlertDialogDescription
                    data-testid="builder-delete-block-description"
                    className="text-[#6B7280]"
                  >
                    {description}
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel
                    data-testid="builder-delete-block-cancel"
                    className="rounded-lg border-[#E5E3DB] text-[#6B7280]"
                  >
                    Cancel
                  </AlertDialogCancel>
                  {isMultiDay && clickedDay ? (
                    <>
                      <AlertDialogAction
                        data-testid="builder-delete-block-confirm-series"
                        onClick={() => confirmDeleteBlock({ scope: "series" })}
                        className="rounded-lg border border-[#B85C5C] text-[#B85C5C] bg-white hover:bg-[#B85C5C]/10"
                      >
                        Remove entire series
                      </AlertDialogAction>
                      <AlertDialogAction
                        data-testid="builder-delete-block-confirm"
                        onClick={() => confirmDeleteBlock({ scope: "occurrence" })}
                        className="rounded-lg bg-[#B85C5C] hover:bg-[#a04e4e] text-white"
                      >
                        Remove {clickedDay.label} only
                      </AlertDialogAction>
                    </>
                  ) : (
                    <AlertDialogAction
                      data-testid="builder-delete-block-confirm"
                      onClick={() => confirmDeleteBlock({ scope: isSessionRow ? "session_row" : "series" })}
                      className="rounded-lg bg-[#B85C5C] hover:bg-[#a04e4e] text-white"
                    >
                      {isSessionRow ? "Remove session" : "Remove block"}
                    </AlertDialogAction>
                  )}
                </AlertDialogFooter>
              </>
            );
          })()}
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
